import { useState, useEffect, useRef, useCallback } from "react";
import { Link } from "react-router-dom";
import {
  ChevronDown, Check, Star, Clock, Shield, Truck,
  ArrowRight, Zap, X, Sparkles, Package, RefreshCw
} from "lucide-react";
import PublicNav from "../components/PublicNav";
import PublicFooter from "../components/PublicFooter";
import { useLocale } from "../context/LocaleContext";
import { useSEO } from "../hooks/useSEO";
import BecomeAMemberButton from "../components/BecomeAMemberButton";
import leftBannerImage from "../assets/image.png";
import rightBannerImage from "../assets/image2.jpeg";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AD_CONFIG = {
  left: {
    image: leftBannerImage,
    badge: "Dry Cleaning",
    title: "Coming soon",
    subtitle: "Premium care for your garments coming soon to Ventura.",
    cta: "Ver membresías",
    ctaUrl: "/membership",
    accent: "#0ea5e9",
    overlayFrom: "#0c4a6e",
    overlayTo: "#0369a1",
  },
  right: {
    image: rightBannerImage,
    badge: "AI Sneaker Pricing",
    title: "Get an instant quote",
    subtitle: "Upload photos, get an AI price estimate for sneaker & shoe cleaning.",
    cta: "Cotizar ahora",
    ctaUrl: "/sneaker-quote",
    accent: "#f59e0b",
    overlayFrom: "#1e1b4b",
    overlayTo: "#312e81",
  },
};

// ─── Hooks ────────────────────────────────────────────────────────────────────
function useInView(threshold = 0.08) {
  const ref = useRef(null);
  const [v, setV] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) { setV(true); obs.disconnect(); } },
      { threshold }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);
  return [ref, v];
}

const ORIGINS = {
  up:    "opacity-0 translate-y-8",
  left:  "opacity-0 translate-x-6",
  right: "opacity-0 -translate-x-6",
  scale: "opacity-0 scale-95",
  blur:  "opacity-0 blur-sm scale-98",
};

const Reveal = ({ children, delay = 0, dir = "up", dur = 600, className = "" }) => {
  const [ref, v] = useInView();
  return (
    <div
      ref={ref}
      className={`${className} transition-all ease-out ${v ? "opacity-100 translate-y-0 translate-x-0 scale-100 blur-0" : ORIGINS[dir]}`}
      style={{ transitionDuration: `${dur}ms`, transitionDelay: `${delay}ms` }}
    >
      {children}
    </div>
  );
};

const Mag = ({ children, className = "", strength = 0.28, as: Tag = "div", ...p }) => {
  const ref = useRef(null);
  const onMove = useCallback((e) => {
    if (!ref.current || window.innerWidth < 1024) return;
    const r = ref.current.getBoundingClientRect();
    ref.current.style.transform = `translate(${(e.clientX - r.left - r.width / 2) * strength}px,${(e.clientY - r.top - r.height / 2) * strength}px)`;
  }, [strength]);
  const onLeave = useCallback(() => {
    if (ref.current) ref.current.style.transform = "translate(0,0)";
  }, []);
  return (
    <Tag
      ref={ref}
      className={className}
      style={{ transition: "transform 500ms cubic-bezier(0.34,1.56,0.64,1)" }}
      onMouseMove={onMove}
      onMouseLeave={onLeave}
      {...p}
    >
      {children}
    </Tag>
  );
};

const Tilt = ({ children, className = "", depth = 5 }) => {
  const ref = useRef(null);
  const [s, setS] = useState({});
  const onMove = useCallback((e) => {
    if (!ref.current || window.innerWidth < 1024) return;
    const r = ref.current.getBoundingClientRect();
    const x = ((e.clientX - r.left) / r.width - 0.5) * depth * 2;
    const y = ((e.clientY - r.top) / r.height - 0.5) * -depth * 2;
    setS({ transform: `perspective(900px) rotateX(${y}deg) rotateY(${x}deg) translateZ(6px)`, transition: "transform 80ms linear" });
  }, [depth]);
  const onLeave = useCallback(() =>
    setS({ transform: "perspective(900px) rotateX(0) rotateY(0) translateZ(0)", transition: "transform 500ms cubic-bezier(0.34,1.56,0.64,1)" }), []);
  return (
    <div ref={ref} style={s} className={className} onMouseMove={onMove} onMouseLeave={onLeave}>
      {children}
    </div>
  );
};

function useCursor() {
  const ring = useRef(null);
  const dot = useRef(null);
  const p = useRef({ x: -200, y: -200 });
  const l = useRef({ x: -200, y: -200 });
  const raf = useRef(null);
  useEffect(() => {
    if (window.innerWidth < 1024) return;
    const fn = (e) => { p.current = { x: e.clientX, y: e.clientY }; };
    window.addEventListener("mousemove", fn, { passive: true });
    const loop = () => {
      l.current.x += (p.current.x - l.current.x) * 0.1;
      l.current.y += (p.current.y - l.current.y) * 0.1;
      if (ring.current) ring.current.style.transform = `translate(${l.current.x - 18}px,${l.current.y - 18}px)`;
      if (dot.current) dot.current.style.transform = `translate(${p.current.x - 3}px,${p.current.y - 3}px)`;
      raf.current = requestAnimationFrame(loop);
    };
    raf.current = requestAnimationFrame(loop);
    return () => { window.removeEventListener("mousemove", fn); cancelAnimationFrame(raf.current); };
  }, []);
  return { ring, dot };
}

const Marquee = ({ items }) => (
  <div className="overflow-hidden py-3 border-y border-primary/10 bg-sky-50/50">
    <style>{`@keyframes mq{from{transform:translateX(0)}to{transform:translateX(-33.333%)}}`}</style>
    <div className="flex gap-8 whitespace-nowrap" style={{ animation: "mq 30s linear infinite" }}>
      {[...items, ...items, ...items].map((it, i) => (
        <span key={i} className="text-[11px] font-bold uppercase tracking-[0.15em] text-primary/45 flex items-center gap-2">
          <span className="w-1 h-1 rounded-full bg-primary/30 inline-block" />{it}
        </span>
      ))}
    </div>
  </div>
);

const PlanBadge = ({ type, t }) => {
  const configs = {
    standard: { label: t ? t("36 h", "36 h") : "36 h", cls: "bg-slate-100 text-slate-500 border border-slate-200" },
    premium:  { label: t ? t("24 h", "24 h") : "24 h", cls: "bg-sky-100 text-sky-700 border border-sky-200" },
    express:  { label: t ? t("Same day", "Mismo día") : "Same day", cls: "bg-amber-100 text-amber-700 border border-amber-200" },
    popular:  { label: t ? t("Most popular", "Más popular") : "Most popular", cls: "bg-sky-100 text-sky-700 border border-sky-200" },
  };
  const c = configs[type] || configs.standard;
  return <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold whitespace-nowrap ${c.cls}`}>{c.label}</span>;
};

const AccordionItem = ({ title, children, isOpen, onClick, variant = "light" }) => {
  const isDark = variant === "dark";
  return (
    <div className={`border-b last:border-0 ${isDark ? "border-white/10" : "border-slate-200/70"}`}>
      <button
        onClick={onClick}
        className="w-full py-4 flex items-center justify-between text-left focus:outline-none min-h-[52px] touch-manipulation"
        aria-expanded={isOpen}
      >
        <span className={`text-sm sm:text-base font-semibold pr-4 transition-colors duration-200 ${isDark ? "text-white/90" : "text-slate-800"}`}>
          {title}
        </span>
        <div className={`flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center transition-all duration-300 border ${
          isOpen
            ? isDark ? "bg-white/15 border-white/25 rotate-180" : "bg-primary border-primary rotate-180"
            : isDark ? "bg-white/8 border-white/12" : "bg-slate-100 border-transparent"
        }`}>
          <ChevronDown className={`w-4 h-4 ${isDark ? "text-white/80" : isOpen ? "text-white" : "text-slate-400"}`} />
        </div>
      </button>
      <div className={`overflow-hidden transition-all duration-500 ease-in-out ${isOpen ? "max-h-[600px] pb-5 opacity-100" : "max-h-0 opacity-0"}`}>
        {children}
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════
//  DARK SECTION — imagen visible + cards glass mate
// ═══════════════════════════════════════════════════════════════
const DarkSection = ({ children, bgImage, scrollY = 0, parallaxStrength = 0.15, tint = "rgba(3,15,35,0.30)" }) => (
  <section className="py-20 sm:py-28 relative overflow-hidden">
    <div
      className="absolute inset-0 will-change-transform"
      style={{
        backgroundImage: `url('${bgImage}')`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        transform: `translateY(${scrollY * parallaxStrength}px) scale(1.05)`,
      }}
    />
    <div className="absolute inset-0" style={{ background: tint }} />
    <div className="absolute inset-0" style={{
      background: "linear-gradient(to bottom, rgba(0,0,0,0.15) 0%, transparent 20%, transparent 80%, rgba(0,0,0,0.20) 100%)"
    }} />
    <div className="relative z-10 max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
      {children}
    </div>
  </section>
);

const GlassCard = ({ children, className = "" }) => (
  <div
    className={`rounded-2xl ${className}`}
    style={{
      background: "rgba(8, 20, 48, 0.42)",
      backdropFilter: "blur(28px) saturate(180%)",
      WebkitBackdropFilter: "blur(28px) saturate(180%)",
      border: "1px solid rgba(255,255,255,0.13)",
      boxShadow: "0 8px 40px rgba(0,0,0,0.28), inset 0 1px 0 rgba(255,255,255,0.08)",
    }}
  >
    {children}
  </div>
);

// ═══════════════════════════════════════════════════════════════
//  AD BANNERS
// ═══════════════════════════════════════════════════════════════
const AdBannerHorizontal = ({ config }) => {
  const [hovered, setHovered] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  if (dismissed) return null;
  return (
    <div
      className="relative rounded-2xl overflow-hidden cursor-pointer"
      style={{
        minHeight: "88px",
        boxShadow: hovered ? `0 12px 40px -8px ${config.accent}55, 0 4px 16px rgba(0,0,0,0.14)` : "0 2px 12px rgba(0,0,0,0.09)",
        transform: hovered ? "translateY(-3px)" : "translateY(0)",
        transition: "all 0.35s cubic-bezier(0.34,1.56,0.64,1)",
      }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onClick={() => window.location.href = config.ctaUrl}
      role="link" tabIndex={0}
      onKeyDown={e => e.key === "Enter" && (window.location.href = config.ctaUrl)}
    >
      <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url('${config.image}')`, transform: hovered ? "scale(1.07)" : "scale(1.02)", transition: "transform 0.6s ease" }} />
      <div className="absolute inset-0" style={{ background: `linear-gradient(100deg, ${config.overlayFrom}f0 0%, ${config.overlayTo}cc 55%, rgba(0,0,0,0.45) 100%)` }} />
      <div className="absolute top-0 left-0 right-0 h-[2px]" style={{ background: `linear-gradient(to right, ${config.accent}, transparent)`, opacity: 0.9 }} />
      <div className="relative h-full flex items-center gap-3 px-4 py-3">
        <div className="flex-1 min-w-0">
          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-bold uppercase tracking-wide mb-1" style={{ background: `${config.accent}28`, border: `1px solid ${config.accent}55`, color: config.accent }}>{config.badge}</span>
          <h3 className="text-white font-black text-base leading-tight" style={{ textShadow: "0 1px 6px rgba(0,0,0,0.5)" }}>{config.title}</h3>
          <p className="text-white/60 text-xs leading-tight line-clamp-2 mt-0.5">{config.subtitle}</p>
        </div>
        <button
          onClick={e => { e.stopPropagation(); setDismissed(true); }}
          className="absolute top-2 right-2 w-6 h-6 rounded-full flex items-center justify-center bg-black/30 hover:bg-black/50 transition-colors touch-manipulation"
          aria-label="Cerrar"
        >
          <X className="w-3 h-3 text-white/70" />
        </button>
      </div>
    </div>
  );
};

const AdBannerVertical = ({ config }) => {
  const [hovered, setHovered] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  if (dismissed) return null;
  return (
    <div className="hidden xl:flex flex-col flex-shrink-0 w-[200px] sticky top-6 self-start" style={{ zIndex: 10 }}>
      <div className="flex items-center justify-between mb-2 px-1">
        <span className="text-[10px] font-bold uppercase tracking-widest text-slate-400/70 select-none">Publicidad</span>
        <button onClick={() => setDismissed(true)} className="text-slate-300 hover:text-slate-500 transition-colors rounded-full p-0.5 hover:bg-slate-100 touch-manipulation" aria-label="Cerrar"><X className="w-3 h-3" /></button>
      </div>
      <div
        className="relative rounded-2xl overflow-hidden cursor-pointer select-none"
        style={{ height: "380px", boxShadow: hovered ? `0 24px 60px -10px ${config.accent}50, 0 8px 24px rgba(0,0,0,0.18)` : "0 4px 20px rgba(0,0,0,0.10)", transform: hovered ? "translateY(-5px) scale(1.015)" : "translateY(0) scale(1)", transition: "all 0.45s cubic-bezier(0.34,1.56,0.64,1)" }}
        onMouseEnter={() => setHovered(true)} onMouseLeave={() => setHovered(false)}
        onClick={() => window.location.href = config.ctaUrl}
        role="link" tabIndex={0}
        onKeyDown={e => e.key === "Enter" && (window.location.href = config.ctaUrl)}
      >
        <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url('${config.image}')`, transform: hovered ? "scale(1.1)" : "scale(1.02)", transition: "transform 0.7s cubic-bezier(0.25,0.46,0.45,0.94)" }} />
        <div className="absolute inset-0" style={{ background: `linear-gradient(155deg, ${config.overlayFrom}e0 0%, ${config.overlayTo}90 55%, transparent 100%)` }} />
        <div className="absolute inset-0" style={{ background: "linear-gradient(to top, rgba(0,0,0,0.88) 0%, rgba(0,0,0,0.2) 55%, transparent 100%)" }} />
        <div className="absolute inset-0 rounded-2xl pointer-events-none transition-all duration-300" style={{ boxShadow: hovered ? `inset 0 0 0 1.5px ${config.accent}90` : "inset 0 0 0 1px rgba(255,255,255,0.08)" }} />
        <div className="absolute top-0 left-0 right-0 h-0.5 transition-all duration-500" style={{ background: `linear-gradient(to right, transparent, ${config.accent}, transparent)`, opacity: hovered ? 1 : 0.3 }} />
        <div className="absolute inset-0 flex flex-col justify-between p-4">
          <div>
            <span className="inline-flex items-center px-2.5 py-1 rounded-full text-[11px] font-bold uppercase tracking-wide backdrop-blur-sm" style={{ background: `${config.accent}22`, border: `1px solid ${config.accent}55`, color: config.accent }}>{config.badge}</span>
          </div>
          <div>
            <div className="w-8 h-0.5 mb-3 rounded-full" style={{ background: config.accent }} />
            <h3 className="text-white font-black leading-[1.1] mb-1.5" style={{ fontSize: "1.5rem", textShadow: "0 2px 12px rgba(0,0,0,0.6)" }}>{config.title}</h3>
            <p className="text-xs font-bold mb-1.5 uppercase tracking-wider" style={{ color: config.accent }}>{config.subtitle}</p>
          </div>
        </div>
      </div>
      <p className="text-center text-[10px] text-slate-400/60 mt-1.5 select-none">Ventura Fresh Laundry</p>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════
//  DYNAMIC ADMIN SERVICES
// ═══════════════════════════════════════════════════════════════
const PRICE_UNIT_LABELS = {
  per_lb:    { en: "/ lb",    es: "/ lb" },
  per_order: { en: "/ order", es: "/ orden" },
  per_month: { en: "/ month", es: "/ mes" },
  per_item:  { en: "/ item",  es: "/ pieza" },
};

const SERVICE_CATEGORY_ICONS = {
  "wash & fold": "🧺", "pickup": "🚚", "delivery": "🚚",
  "dry cleaning": "👔", "shoe": "👟", "ironing": "🔄",
  "bedding": "🛏️", "membership": "⭐", "express": "⚡",
  "commercial": "🏢", "airbnb": "🏠",
};

function getServiceIcon(name = "", category = "") {
  const search = `${name} ${category}`.toLowerCase();
  for (const [key, icon] of Object.entries(SERVICE_CATEGORY_ICONS)) {
    if (search.includes(key)) return icon;
  }
  return "✨";
}

function groupByCategory(services) {
  const groups = {};
  for (const svc of services) {
    const cat = svc.category || "Other";
    if (!groups[cat]) groups[cat] = [];
    groups[cat].push(svc);
  }
  return groups;
}

const AdminServiceCard = ({ service, t, locale }) => {
  const [h, setH] = useState(false);
  const icon = getServiceIcon(service.name, service.category);
  const unitLabel = PRICE_UNIT_LABELS[service.price_unit] || { en: "", es: "" };
  const unit = locale === "es" ? unitLabel.es : unitLabel.en;
  return (
    <Tilt depth={3}>
      <div
        className={`relative bg-white rounded-2xl h-full flex flex-col overflow-hidden border transition-all duration-300 ${h ? "border-primary/30 shadow-xl shadow-sky-100/60" : "border-slate-100 shadow-md"}`}
        onMouseEnter={() => setH(true)} onMouseLeave={() => setH(false)}
      >
        <div className={`absolute top-0 left-6 right-6 h-px bg-gradient-to-r from-transparent via-primary to-transparent transition-all duration-500 ${h ? "opacity-100" : "opacity-0"}`} />
        <div className={`absolute inset-0 bg-gradient-to-br from-sky-50/40 to-transparent transition-opacity duration-500 pointer-events-none ${h ? "opacity-100" : "opacity-0"}`} />
        <div className="relative p-5 flex flex-col gap-3 flex-grow">
          <div className="flex items-start justify-between gap-3">
            <div className={`w-11 h-11 flex items-center justify-center text-xl rounded-xl flex-shrink-0 transition-all duration-300 ${h ? "bg-primary/15 scale-110 rotate-3" : "bg-slate-50"}`}>{icon}</div>
            {service.price != null && (
              <div className="text-right flex-shrink-0">
                <div className="text-xl font-black text-primary leading-none">${service.price}</div>
                <div className="text-[11px] text-slate-400 font-medium mt-0.5">{unit}</div>
              </div>
            )}
          </div>
          <div className="flex-grow">
            <h3 className={`text-base font-bold mb-1 transition-colors duration-200 leading-tight ${h ? "text-primary" : "text-slate-900"}`}>{service.name}</h3>
            {service.description && <p className="text-slate-400 text-xs leading-relaxed line-clamp-3">{service.description}</p>}
          </div>
        </div>
        <div className="relative px-5 pb-5">
          <Link to="/schedule-pickup">
            <button className="group w-full flex items-center justify-center gap-2 bg-primary text-white rounded-xl px-4 py-2.5 text-xs font-bold uppercase tracking-wider hover:bg-primary/90 active:scale-95 transition-all duration-300 shadow-sm shadow-primary/20 overflow-hidden relative touch-manipulation" style={{ minHeight: "40px" }}>
              <span className="relative z-10 flex items-center gap-2">{t("Book Now", "Reservar")}<ArrowRight className="w-3.5 h-3.5 transition-transform duration-200 group-hover:translate-x-1" /></span>
              <span className="absolute inset-0 bg-gradient-to-r from-transparent via-white/15 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-700" />
            </button>
          </Link>
        </div>
      </div>
    </Tilt>
  );
};

const AdminServicesSection = ({ t, locale }) => {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    const load = async () => {
      try {
        const res = await fetch(`${API}/public/services?active_only=true`);
        if (res.ok) setServices(await res.json());
      } catch { } finally { setLoading(false); }
    };
    load();
  }, []);
  if (loading || services.length === 0) return null;
  const groups = groupByCategory(services);
  return (
    <section className="py-12 sm:py-20 bg-gradient-to-b from-slate-50/60 to-white relative overflow-hidden">
      <div className="absolute inset-0 opacity-[0.35]" style={{ backgroundImage: "radial-gradient(rgba(14,165,233,0.07) 1px,transparent 1px)", backgroundSize: "24px 24px" }} />
      <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 lg:px-12">
        <Reveal dir="blur">
          <div className="flex items-center justify-center gap-3 mb-3">
            <div className="h-px w-8 bg-primary/30" /><Package className="w-4 h-4 text-primary/50" /><div className="h-px w-8 bg-primary/30" />
          </div>
          <p className="text-center text-[11px] font-bold uppercase tracking-[0.18em] text-primary/50 mb-3">{t("Additional Services", "Servicios Adicionales")}</p>
        </Reveal>
        <Reveal delay={80}><h2 className="text-2xl sm:text-4xl lg:text-5xl font-bold text-slate-900 text-center mb-3 leading-tight">{t("More ways we", "Más formas en que")}<span className="block text-primary font-bold">{t("can help.", "podemos ayudarte.")}</span></h2></Reveal>
        <Reveal delay={140}><p className="text-slate-500 text-center mb-10 max-w-xl mx-auto text-sm sm:text-base">{t("Specialized care for every garment and need.", "Cuidado especializado para cada prenda y necesidad.")}</p></Reveal>
        {Object.entries(groups).map(([category, svcs], gi) => (
          <div key={category} className="mb-10 last:mb-0">
            {Object.keys(groups).length > 1 && (
              <Reveal delay={gi * 60}>
                <div className="flex items-center gap-3 mb-5">
                  <h3 className="text-sm font-bold uppercase tracking-widest text-slate-400">{category}</h3>
                  <div className="flex-1 h-px bg-slate-200" />
                </div>
              </Reveal>
            )}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-5">
              {svcs.map((svc, i) => (
                <Reveal key={svc.id} delay={gi * 60 + i * 60} dir="up" dur={600}>
                  <AdminServiceCard service={svc} t={t} locale={locale} />
                </Reveal>
              ))}
            </div>
          </div>
        ))}
        <Reveal delay={200} dir="scale">
          <div className="mt-10 text-center">
            <Link to="/schedule-pickup">
              <Mag as="div" strength={0.2} className="inline-flex items-center gap-2 overflow-hidden relative bg-primary text-white rounded-full px-8 py-3.5 text-sm font-bold uppercase tracking-widest shadow-lg shadow-primary/25 cursor-pointer hover:-translate-y-0.5 transition-transform duration-300 active:scale-95 group touch-manipulation">
                <span className="relative z-10 flex items-center gap-2">{t("Book Any Service", "Reservar Cualquier Servicio")}<ArrowRight className="w-4 h-4 transition-transform duration-200 group-hover:translate-x-1" /></span>
                <span className="absolute inset-0 bg-gradient-to-r from-transparent via-white/15 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-700" />
              </Mag>
            </Link>
          </div>
        </Reveal>
      </div>
    </section>
  );
};

// ─── Service Cards ────────────────────────────────────────────────────────────
const PickupDeliveryServiceCardHead = ({ t }) => {
  const [h, setH] = useState(false);
  const tiers = [
    { name: t("Standard", "Estándar"), time: "36 h" },
    { name: t("Premium", "Premium"), time: "24 h" },
    { name: t("Express", "Express"), time: t("Same day", "Mismo día") },
  ];
  return (
    <Tilt depth={4}>
      <div className={`relative bg-white rounded-2xl h-full flex flex-col overflow-hidden border transition-all duration-300 ${h ? "border-primary/30 shadow-2xl shadow-sky-100/60" : "border-slate-100 shadow-lg"}`} onMouseEnter={() => setH(true)} onMouseLeave={() => setH(false)}>
        <div className={`absolute top-0 left-6 right-6 h-px bg-gradient-to-r from-transparent via-primary to-transparent transition-all duration-500 ${h ? "opacity-100" : "opacity-0"}`} />
        <div className={`absolute inset-0 bg-gradient-to-br from-sky-50/40 to-transparent transition-opacity duration-500 pointer-events-none ${h ? "opacity-100" : "opacity-0"}`} />
        <div className="relative px-5 sm:px-7 pt-6 pb-4 border-b border-slate-100">
          <div className={`w-12 h-12 flex items-center justify-center text-2xl mb-3 rounded-2xl transition-all duration-300 ${h ? "bg-primary/15 scale-110 rotate-3" : "bg-slate-50"}`}>🚚</div>
          <h3 className={`text-xl sm:text-2xl font-bold mb-1 transition-colors duration-200 ${h ? "text-primary" : "text-slate-900"}`}>{t("Pickup & Delivery", "Recogida y Entrega")}</h3>
          <p className="text-slate-400 text-sm">{t("Laundry, fully automated on your schedule.", "Lavandería automatizada en tu horario.")}</p>
        </div>
        <div className="grid grid-cols-3 divide-x divide-slate-100 border-b border-slate-100">
          {tiers.map((tier, idx) => (
            <div key={idx} className="px-3 py-4 text-center">
              <div className="text-sm font-semibold text-slate-700 mb-1">{tier.name}</div>
              <div className="text-xl font-black text-primary">{tier.time}</div>
            </div>
          ))}
        </div>
        <div className="relative px-5 py-3 bg-slate-50 border-t border-slate-100">
          <ul className="space-y-1.5">
            <li className="text-xs text-slate-600 flex items-start gap-2"><span className="text-green-600 text-sm mt-0.5">✅</span>{t("FREE delivery (0–3 miles)", "Entrega GRATIS (0–3 millas)")}</li>
            <li className="text-xs text-slate-600 flex items-start gap-2"><span className="text-slate-400 text-sm mt-0.5">🚗</span>{t("3–15 Miles | $1.99–$8.99", "3–15 Miles | $1.99–$8.99")}</li>
            <li className="text-xs text-slate-600 flex items-start gap-2"><span className="text-amber-500 text-sm mt-0.5">⚠️</span>{t("Min. order $40", "Pedido mínimo $40")}</li>
          </ul>
        </div>
        <div className="relative px-5 py-3 bg-white border-t border-slate-100 flex justify-between items-center">
          <span className="text-sm font-semibold text-slate-600">{t("Regular", "Regular")}</span>
          <span className="text-sm font-semibold text-sky-600">{t("Member", "Miembro")}</span>
        </div>
        <div className="relative px-5 sm:px-7 pb-6 pt-2">
          <Link to="/schedule-pickup">
            <button className="group w-full flex items-center justify-center gap-2 bg-primary text-white rounded-xl px-6 py-3.5 text-sm font-bold uppercase tracking-wider hover:bg-primary/90 active:scale-95 transition-all duration-300 shadow-md shadow-primary/20 overflow-hidden relative touch-manipulation" style={{ minHeight: "48px" }}>
              <span className="relative z-10 flex items-center gap-2">{t("SCHEDULE PICK-UP", "PROGRAMAR RECOGIDA")}<ArrowRight className="w-4 h-4 transition-transform duration-200 group-hover:translate-x-1" /></span>
              <span className="absolute inset-0 bg-gradient-to-r from-transparent via-white/15 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-700" />
            </button>
          </Link>
        </div>
      </div>
    </Tilt>
  );
};

const WashFoldServiceCardHead = ({ t }) => {
  const [h, setH] = useState(false);
  const tiers = [
    { name: t("Standard", "Estándar"), time: "24–36 h" },
    { name: t("Premium", "Premium"), time: "12–24 h", isPopular: true },
    { name: t("Express", "Express"), time: t("Same day", "Mismo día") },
  ];
  return (
    <Tilt depth={4}>
      <div className={`relative bg-white rounded-2xl h-full flex flex-col overflow-hidden border transition-all duration-300 ${h ? "border-primary/30 shadow-2xl shadow-sky-100/60" : "border-slate-100 shadow-lg"}`} onMouseEnter={() => setH(true)} onMouseLeave={() => setH(false)}>
        <div className={`absolute top-0 left-6 right-6 h-px bg-gradient-to-r from-transparent via-primary to-transparent transition-all duration-500 ${h ? "opacity-100" : "opacity-0"}`} />
        <div className={`absolute inset-0 bg-gradient-to-br from-sky-50/40 to-transparent transition-opacity duration-500 pointer-events-none ${h ? "opacity-100" : "opacity-0"}`} />
        <div className="relative px-5 sm:px-7 pt-6 pb-4 border-b border-slate-100">
          <div className={`w-12 h-12 flex items-center justify-center text-2xl mb-3 rounded-2xl transition-all duration-300 ${h ? "bg-primary/15 scale-110 rotate-3" : "bg-slate-50"}`}>🧺</div>
          <h3 className={`text-xl sm:text-2xl font-bold mb-1 transition-colors duration-200 ${h ? "text-primary" : "text-slate-900"}`}>{t("Wash • Dry • Fold", "Lavar • Secar • Doblar")}</h3>
          <p className="text-slate-400 text-sm">{t("Professional care without lifting a finger.", "Cuidado profesional sin mover un dedo.")}</p>
        </div>
        <div className="grid grid-cols-3 divide-x divide-slate-100 border-b border-slate-100">
          {tiers.map((tier, idx) => (
            <div key={idx} className="px-3 py-4 text-center">
              <div className="text-sm font-semibold text-slate-700 mb-1">{tier.name}{tier.isPopular && <span className="ml-1 text-primary text-xs">⭐</span>}</div>
              <div className="text-xs text-slate-400">{tier.time}</div>
            </div>
          ))}
        </div>
        <div className="relative px-5 py-2 bg-slate-50 border-t border-slate-100 text-xs text-slate-500">{t("Professional care · Minimum 10 lb per order", "Cuidado profesional · Mínimo 10 lb por orden")}</div>
        <div className="relative px-5 py-2 bg-slate-50 border-t border-slate-100 text-xs text-slate-500">{t("Monday – Sunday · 8:00 AM – 6:00 PM", "Lunes – Domingo · 8:00 AM – 6:00 PM")}</div>
        <div className="relative px-5 sm:px-7 pb-6 pt-4">
          <Link to="/wash-fold">
            <button className="group w-full flex items-center justify-center gap-2 bg-slate-900 text-white rounded-xl px-6 py-3.5 text-sm font-bold uppercase tracking-wider hover:bg-slate-800 active:scale-95 transition-all duration-300 shadow-md overflow-hidden relative touch-manipulation" style={{ minHeight: "48px" }}>
              <span className="relative z-10 flex items-center gap-2">{t("DROP OFF / SCHEDULE", "ENTREGA / PROGRAMAR")}<ArrowRight className="w-4 h-4 transition-transform duration-200 group-hover:translate-x-1" /></span>
              <span className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-700" />
            </button>
          </Link>
        </div>
      </div>
    </Tilt>
  );
};

const SneakerServiceCardHead = ({ t }) => {
  const [h, setH] = useState(false);
  const tiers = [
    { name: t("1st pair", "1er par"), price: "$12" },
    { name: t("2nd pair", "2do par"), price: "$10", isPopular: true },
    { name: t("3rd+ pair", "3er par+"), price: "$8" },
  ];
  return (
    <Tilt depth={4}>
      <div className={`relative bg-white rounded-2xl h-full flex flex-col overflow-hidden border transition-all duration-300 ${h ? "border-violet-300 shadow-2xl shadow-violet-100/60" : "border-slate-100 shadow-lg"}`} onMouseEnter={() => setH(true)} onMouseLeave={() => setH(false)}>
        <div className={`absolute top-0 left-6 right-6 h-px bg-gradient-to-r from-transparent via-violet-500 to-transparent transition-all duration-500 ${h ? "opacity-100" : "opacity-0"}`} />
        <div className={`absolute inset-0 bg-gradient-to-br from-violet-50/40 to-transparent transition-opacity duration-500 pointer-events-none ${h ? "opacity-100" : "opacity-0"}`} />
        <div className="relative px-5 sm:px-7 pt-6 pb-4 border-b border-slate-100">
          <div className={`w-12 h-12 flex items-center justify-center text-2xl mb-3 rounded-2xl transition-all duration-300 ${h ? "bg-violet-100 scale-110 rotate-3" : "bg-slate-50"}`}>👟</div>
          <div className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-violet-100 text-violet-600 text-[10px] font-bold uppercase tracking-wide mb-2">
            <Sparkles className="w-3 h-3" />{t("AI-Powered", "Con IA")}
          </div>
          <h3 className={`text-xl sm:text-2xl font-bold mb-1 transition-colors duration-200 ${h ? "text-violet-600" : "text-slate-900"}`}>{t("Sneaker & Shoe Cleaning", "Limpieza de Tenis y Calzado")}</h3>
          <p className="text-slate-400 text-sm">{t("Instant AI price estimate from your photos.", "Precio estimado al instante con IA desde tus fotos.")}</p>
        </div>
        <div className="grid grid-cols-3 divide-x divide-slate-100 border-b border-slate-100">
          {tiers.map((tier, idx) => (
            <div key={idx} className="px-3 py-4 text-center">
              <div className="text-sm font-semibold text-slate-700 mb-1">{tier.name}{tier.isPopular && <span className="ml-1 text-violet-600 text-xs">⭐</span>}</div>
              <div className="text-xs font-bold text-violet-600">{tier.price}</div>
            </div>
          ))}
        </div>
        <div className="relative px-5 py-2 bg-slate-50 border-t border-slate-100 text-xs text-slate-500">{t("In-store drop-off only · no pickup or delivery", "Solo entrega en tienda · sin recogida ni entrega a domicilio")}</div>
        <div className="relative px-5 py-2 bg-slate-50 border-t border-slate-100 text-xs text-slate-500">{t("Photo evidence at every step", "Evidencia fotográfica en cada paso")}</div>
        <div className="relative px-5 sm:px-7 pb-6 pt-4 mt-auto">
          <Link to="/sneaker-quote">
            <button className="group w-full flex items-center justify-center gap-2 bg-violet-600 text-white rounded-xl px-6 py-3.5 text-sm font-bold uppercase tracking-wider hover:bg-violet-700 active:scale-95 transition-all duration-300 shadow-md shadow-violet-200 overflow-hidden relative touch-manipulation" style={{ minHeight: "48px" }}>
              <span className="relative z-10 flex items-center gap-2">{t("GET INSTANT QUOTE", "COTIZA YA")}<ArrowRight className="w-4 h-4 transition-transform duration-200 group-hover:translate-x-1" /></span>
              <span className="absolute inset-0 bg-gradient-to-r from-transparent via-white/15 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-700" />
            </button>
          </Link>
        </div>
      </div>
    </Tilt>
  );
};

// ═══════════════════════════════════════════════════════════════
//  COMPONENTES DINÁMICOS QUE CARGAN DATOS DEL BACKEND
// ═══════════════════════════════════════════════════════════════

const DynamicExpressServiceBlock = ({ t }) => {
  const [chips, setChips] = useState([
    { label: "In-Store", label_es: "En Tienda", price: "$2.75/lb" },
    { label: "Members P&D", label_es: "Miembros R&E", price: "$3.00/lb" },
    { label: "Regular P&D", label_es: "Regular R&E", price: "$3.25/lb" }
  ]);
  const [features, setFeatures] = useState([
    "Priority processing", "Fast turnaround", "Limited capacity"
  ]);
  const [loading, setLoading] = useState(true);
  const { locale } = useLocale();

  useEffect(() => {
    const loadExpressConfig = async () => {
      try {
        const res = await fetch(`${API}/public/services-page-config`);
        if (res.ok) {
          const config = await res.json();
          if (config.express_chips && config.express_chips.length > 0) {
            setChips(config.express_chips);
          }
          if (config.express_features && config.express_features.length > 0) {
            setFeatures(config.express_features);
          }
        }
      } catch (error) {
        console.error("Error loading express config:", error);
      } finally {
        setLoading(false);
      }
    };
    loadExpressConfig();
  }, []);

  const getChipLabel = (chip) => {
    return locale === "es" && chip.label_es ? chip.label_es : chip.label;
  };

  if (loading) {
    return (
      <div className="my-6 sm:my-10 rounded-2xl overflow-hidden shadow-2xl bg-slate-800 p-12 text-center">
        <RefreshCw className="w-6 h-6 animate-spin mx-auto text-white" />
      </div>
    );
  }

  return (
    <div className="my-6 sm:my-10 rounded-2xl overflow-hidden shadow-2xl">
      <div className="bg-gradient-to-r from-sky-900 to-blue-900 p-6 sm:p-8 md:p-12 text-center text-white relative">
        <div className="absolute inset-0 opacity-10 bg-[url('https://images.unsplash.com/photo-1545173168-9f1947eebb7f?w=1920&h=1080&fit=crop')] bg-cover bg-center" />
        <div className="relative z-10">
          <div className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-sm rounded-full px-4 py-1.5 mb-4 sm:mb-6">
            <Zap className="w-4 h-4 text-yellow-300 fill-yellow-300 flex-shrink-0" />
            <span className="text-[11px] font-bold uppercase tracking-wider">{t("Express Service", "Servicio Express")}</span>
          </div>
          <h3 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-2 sm:mb-3">{t("⚡ Need it today?", "⚡ ¿Lo necesitas hoy?")}</h3>
          <p className="text-white/80 text-sm sm:text-lg mb-5 sm:mb-8">{t("Same Day Service Available", "Servicio disponible el mismo día")}</p>
          <div className="flex flex-col sm:flex-row justify-center gap-2 sm:gap-4 mb-5 sm:mb-8">
            {chips.map((chip, i) => (
              <div key={i} className="bg-white/15 backdrop-blur-sm border border-white/25 rounded-xl px-5 py-3 flex sm:flex-col items-center justify-between sm:justify-center gap-2 sm:gap-1 sm:min-w-[110px]">
                <p className="text-xs text-white/60 uppercase tracking-wider">{getChipLabel(chip)}</p>
                <p className="text-xl font-black text-white">{chip.price}</p>
              </div>
            ))}
          </div>
          <ul className="flex flex-wrap justify-center gap-3 sm:gap-6 text-xs sm:text-sm mb-5 sm:mb-8">
            {features.map((item, i) => (
              <li key={i} className="flex items-center gap-1.5 sm:gap-2">
                <Check className="w-4 h-4 text-sky-300 flex-shrink-0" />
                {t(item, item)}
              </li>
            ))}
          </ul>
          <Link to="/schedule-pickup?express=true">
            <Mag as="div" strength={0.2} className="inline-flex items-center gap-2 bg-white text-sky-900 rounded-full px-6 sm:px-8 py-3 font-bold text-sm uppercase tracking-wider shadow-lg hover:shadow-xl transition-all hover:-translate-y-0.5 active:scale-95 touch-manipulation" style={{ minHeight: "48px" }}>
              {t("Book Express Service", "Reservar Servicio Express")} <ArrowRight className="w-4 h-4" />
            </Mag>
          </Link>
        </div>
      </div>
    </div>
  );
};

const DynamicSelfServiceTable = ({ t, locale }) => {
  const [washerPrices, setWasherPrices] = useState([]);
  const [dryerPrices, setDryerPrices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hours, setHours] = useState({ open: "6:00 AM", close: "10:00 PM" });

  useEffect(() => {
    const loadPrices = async () => {
      try {
        const res = await fetch(`${API}/public/services-page-config`);
        if (res.ok) {
          const config = await res.json();
          setWasherPrices(config.washers || []);
          setDryerPrices(config.dryers || []);
          setHours({
            open: config.self_service_hours_open || "6:00 AM",
            close: config.self_service_hours_close || "10:00 PM"
          });
        }
      } catch (error) {
        console.error("Error loading self-service prices:", error);
      } finally {
        setLoading(false);
      }
    };
    loadPrices();
  }, []);

  if (loading) {
    return (
      <div className="bg-white rounded-2xl border border-slate-100 shadow-lg p-8 text-center">
        <RefreshCw className="w-6 h-6 animate-spin mx-auto text-primary" />
      </div>
    );
  }

  return (
    <div className="bg-white rounded-2xl overflow-hidden border border-slate-100 shadow-lg hover:shadow-xl hover:-translate-y-0.5 transition-all duration-300 h-full flex flex-col">
      <div className="px-5 pt-5 pb-3 border-b border-slate-100">
        <p className="text-[11px] font-bold uppercase tracking-widest text-primary/50 mb-1">{t("Walk-in", "Presencial")}</p>
        <h3 className="text-lg sm:text-xl font-bold text-slate-900">{t("🏪 Self-Service", "🏪 Autoservicio")}</h3>
        <p className="text-slate-400 text-xs mt-1">
          {t(`Open ${hours.open} – ${hours.close}`, `Abierto ${hours.open} – ${hours.close}`)}
        </p>
      </div>
      <div className="flex-grow grid grid-cols-2 divide-x divide-slate-100">
        <div className="p-4">
          <p className="text-[11px] font-bold uppercase tracking-wider text-slate-500 mb-3 flex items-center gap-1.5">
            <span className="w-0.5 h-4 bg-primary rounded-full inline-block flex-shrink-0" />{t("Washers", "Lavadoras")}
          </p>
          <table className="w-full">
            <tbody>
              {washerPrices.map((it, i) => (
                <tr key={i} className="border-b border-slate-50 last:border-0 hover:bg-sky-50/30 transition-colors group">
                  <td className="py-2 text-xs text-slate-500 group-hover:text-slate-700 pr-1">
                    {locale === "es" && it.size_es ? it.size_es : it.size}
                  </td>
                  <td className="py-2 text-right text-xs font-bold text-slate-800 group-hover:text-primary transition-colors whitespace-nowrap">{it.price}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="p-4">
          <p className="text-[11px] font-bold uppercase tracking-wider text-slate-500 mb-3 flex items-center gap-1.5">
            <span className="w-0.5 h-4 bg-sky-300 rounded-full inline-block flex-shrink-0" />{t("Dryers", "Secadoras")}
          </p>
          <table className="w-full">
            <thead>
              <tr>
                <th className="text-left py-1 text-[10px] font-bold uppercase tracking-wider text-slate-400">{t("Size", "Tamaño")}</th>
                <th className="text-left py-1 text-[10px] font-bold uppercase tracking-wider text-slate-400">{t("Time", "Tiempo")}</th>
                <th className="text-right py-1 text-[10px] font-bold uppercase tracking-wider text-slate-400">{t("Price", "Precio")}</th>
              </tr>
            </thead>
            <tbody>
              {dryerPrices.map((it, i) => (
                <tr key={i} className="border-b border-slate-50 last:border-0 hover:bg-sky-50/30 transition-colors group">
                  <td className="py-2 text-xs text-slate-500 group-hover:text-slate-700 pr-1 whitespace-nowrap">{it.size}</td>
                  <td className="py-2 text-xs text-slate-500 group-hover:text-slate-700 pr-1 whitespace-nowrap">{it.time}</td>
                  <td className="py-2 text-right text-xs font-bold text-slate-800 group-hover:text-primary transition-colors whitespace-nowrap">{it.price}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="text-[11px] text-slate-400 mt-2">{t("+5 min extra: $0.25", "+5 min extra: $0.25")}</p>
        </div>
      </div>
    </div>
  );
};

const DynamicPerPieceTable = ({ t, locale }) => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadCategories = async () => {
      try {
        const res = await fetch(`${API}/public/services-page-config`);
        if (res.ok) {
          const config = await res.json();
          setCategories(config.per_piece_categories || []);
        }
      } catch (error) {
        console.error("Error loading per-piece categories:", error);
      } finally {
        setLoading(false);
      }
    };
    loadCategories();
  }, []);

  if (loading) {
    return (
      <div className="bg-white rounded-2xl border border-slate-100 shadow-lg p-8 text-center">
        <RefreshCw className="w-6 h-6 animate-spin mx-auto text-primary" />
      </div>
    );
  }

  const mainCategories = categories.slice(0, 3);
  const extraCategories = categories.slice(3);

  return (
    <div className="bg-white rounded-2xl overflow-hidden border border-slate-100 shadow-lg hover:shadow-xl hover:-translate-y-0.5 transition-all duration-300 h-full flex flex-col">
      <div className="px-5 pt-5 pb-3 border-b border-slate-100">
        <p className="text-[11px] font-bold uppercase tracking-widest text-primary/50 mb-1">{t("Individual Items", "Artículos Individuales")}</p>
        <h3 className="text-lg sm:text-xl font-bold text-slate-900">{t("🧺 Per Piece Pricing", "🧺 Precio por Pieza")}</h3>
      </div>
      <div className="flex-grow flex flex-col">
        <div className="grid grid-cols-1 sm:grid-cols-3 divide-y sm:divide-y-0 sm:divide-x divide-slate-100">
          {mainCategories.map((cat, ci) => (
            <div key={ci} className="p-4">
              <p className="text-[11px] font-bold uppercase tracking-wider text-slate-500 border-b-2 border-primary/15 pb-2 mb-3">
                {locale === "es" && cat.category_es ? cat.category_es : cat.category}
              </p>
              <table className="w-full">
                <tbody>
                  {cat.items.map((it, i) => (
                    <tr key={i} className="border-b border-slate-50 last:border-0 hover:bg-sky-50/30 transition-colors group">
                      <td className="py-2 text-xs text-slate-500 group-hover:text-slate-700 pr-2 leading-tight">
                        {locale === "es" && it.name_es ? it.name_es : it.name}
                      </td>
                      <td className="py-2 text-right text-xs font-bold text-slate-800 group-hover:text-primary transition-colors whitespace-nowrap">{it.price}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ))}
        </div>
        {mainCategories.length >= 3 && (
          <div className="px-4 py-2 text-xs text-amber-600 bg-amber-50 border border-amber-200 rounded-lg mx-4 my-2">
            💡 {t("Bed sheet sets and regular clothing are charged by weight at our standard Wash & Fold rate.", "Los juegos de sábanas y la ropa común se cobran por peso a nuestra tarifa estándar.")}
          </div>
        )}
        {extraCategories.length > 0 && (
          <div className="border-t border-slate-100 p-4">
            {extraCategories.map((cat, ci) => (
              <div key={ci}>
                <p className="text-[11px] font-bold uppercase tracking-wider text-slate-500 border-b-2 border-primary/15 pb-2 mb-3">
                  {locale === "es" && cat.category_es ? cat.category_es : cat.category}
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-x-4 gap-y-1">
                  {cat.items.map((it, i) => (
                    <div key={i} className="flex justify-between items-center py-2 border-b border-slate-50 last:border-0 hover:bg-sky-50/30 transition-colors px-1">
                      <span className="text-xs text-slate-600">
                        {locale === "es" && it.name_es ? it.name_es : it.name}
                      </span>
                      <span className="text-xs font-bold text-slate-800 whitespace-nowrap">{it.price}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      <div className="px-5 py-3 bg-slate-50 border-t border-slate-100 text-center">
        <Link to="/schedule-pickup" className="inline-flex items-center gap-1.5 text-sm text-primary hover:text-sky-600 font-semibold transition-colors group">
          {t("Special items? Contact us", "¿Artículos especiales? Contáctanos")}
          <ArrowRight className="w-3.5 h-3.5 transition-transform duration-200 group-hover:translate-x-1" />
        </Link>
      </div>
    </div>
  );
};

const DynamicDarkSection = ({ sectionType, defaultData, scrollY = 0, t }) => {
  const [sectionData, setSectionData] = useState(defaultData);
  const [loading, setLoading] = useState(true);
  const { locale } = useLocale();

  useEffect(() => {
    const loadSection = async () => {
      try {
        const res = await fetch(`${API}/public/services-page-config`);
        if (res.ok) {
          const config = await res.json();
          const sectionKey = `${sectionType}_section`;
          if (config[sectionKey]) {
            setSectionData(config[sectionKey]);
          }
        }
      } catch (error) {
        console.error(`Error loading ${sectionType} section:`, error);
      } finally {
        setLoading(false);
      }
    };
    loadSection();
  }, [sectionType]);

  const getText = (field) => {
    if (locale === "es" && sectionData[`${field}_es`]) {
      return sectionData[`${field}_es`];
    }
    return sectionData[field] || defaultData[field];
  };

  const getFeatures = () => {
    if (locale === "es" && sectionData.features_es && sectionData.features_es.length > 0) {
      return sectionData.features_es;
    }
    return sectionData.features || defaultData.features || [];
  };

  const bgImage = sectionData.bg_image_url || defaultData.bg_image_url;
  const tint = sectionData.tint || defaultData.tint || "rgba(3, 15, 40, 0.68)";

  if (loading) {
    return (
      <section className="py-20 sm:py-28 relative overflow-hidden bg-slate-800">
        <div className="relative z-10 max-w-3xl mx-auto px-4 text-center">
          <RefreshCw className="w-8 h-8 animate-spin mx-auto text-white/50" />
        </div>
      </section>
    );
  }

  return (
    <DarkSection bgImage={bgImage} scrollY={scrollY} parallaxStrength={0.03} tint={tint}>
      <Reveal dir="blur">
        <div className="flex items-center justify-center gap-2 mb-4">
          <div className="h-px w-8 bg-white/30" />
          <span className="text-[11px] font-bold uppercase tracking-[0.18em] text-white/70">
            {sectionType === "airbnb" ? t("Airbnb & Rentals", "Airbnb y Alquileres") : 
             sectionType === "b2b" ? t("Commercial Services", "Servicios Comerciales") : 
             t("Commercial Services", "Servicios Comerciales")}
          </span>
          <div className="h-px w-8 bg-white/30" />
        </div>
      </Reveal>
      <Reveal delay={80}>
        <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-white text-center mb-4 leading-tight" style={{ textShadow: "0 2px 20px rgba(0,0,0,0.4)" }}>
          {getText("heading")}
        </h2>
      </Reveal>
      <Reveal delay={160}>
        <p className="text-white/80 text-center mb-8 max-w-lg mx-auto text-sm sm:text-base leading-relaxed" style={{ textShadow: "0 1px 8px rgba(0,0,0,0.5)" }}>
          {getText("subheading")}
        </p>
      </Reveal>
      <Reveal delay={240} dir="scale">
        <GlassCard className="p-4 sm:p-6 mb-8">
          <AccordionItem 
            title={t("Key Features", "Características Clave")} 
            isOpen={true} 
            onClick={() => {}} 
            variant="dark"
          >
            <ul className="space-y-2.5 pt-1">
              {getFeatures().map((item, i) => (
                <li key={i} className="flex items-start gap-2.5 text-sm text-white/75">
                  <Check className="w-4 h-4 text-sky-400 flex-shrink-0 mt-0.5" />{item}
                </li>
              ))}
            </ul>
          </AccordionItem>
        </GlassCard>
      </Reveal>
      <Reveal delay={340}>
        <div className="text-center">
          <Link to={sectionData.cta_url || defaultData.cta_url || "/schedule-pickup"}>
            <Mag as="div" strength={0.2} className="inline-flex items-center gap-2 overflow-hidden relative bg-white rounded-full px-7 sm:px-10 py-3.5 sm:py-4 text-sm font-bold uppercase tracking-widest shadow-xl cursor-pointer hover:-translate-y-0.5 transition-transform duration-300 active:scale-95 group touch-manipulation">
              <span className="relative z-10 flex items-center gap-2">{getText("cta_label")}<ArrowRight className="w-4 h-4 transition-transform duration-200 group-hover:translate-x-1" /></span>
              <span className="absolute inset-0 bg-gradient-to-r from-transparent via-primary/8 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-700" />
            </Mag>
          </Link>
        </div>
      </Reveal>
    </DarkSection>
  );
};

// Datos por defecto para secciones oscuras
const DARK_SECTION_DEFAULTS = {
  airbnb: {
    heading: "Premium Laundry for Airbnb Hosts.",
    heading_es: "Lavandería Premium para Anfitriones Airbnb.",
    subheading: "Spotless linens. Five-star guest experiences. Zero hassle.",
    subheading_es: "Ropa de cama impecable. Experiencias de cinco estrellas. Cero complicaciones.",
    features: ["Customized programs for Airbnb hosts", "Professional cleaning & sanitization", "Scheduled pickup aligned with turnover", "Consistent quality for 5-star reviews", "Save time, eliminate laundry stress"],
    features_es: ["Programas personalizados para anfitriones", "Limpieza y sanitización profesional", "Recogida alineada con tu calendario", "Calidad constante para reseñas de 5 estrellas", "Ahorra tiempo, elimina el estrés"],
    cta_label: "SCHEDULE PICK-UP",
    cta_label_es: "PROGRAMAR RECOGIDA",
    cta_url: "/schedule-pickup",
    bg_image_url: "https://images.unsplash.com/photo-1561053720-76cd73ff22c3?q=80&w=1170&auto=format&fit=crop",
    tint: "rgba(3, 15, 40, 0.68)",
  },
  b2b: {
    heading: "High-Performance B2B Laundry.",
    heading_es: "Alto Rendimiento Lavandería B2B.",
    subheading: "Reliable, scalable, professional — built to handle volume every day.",
    subheading_es: "Confiable, escalable, profesional — para manejar volumen todos los días.",
    features: ["Customized programs for all business sizes", "Commercial-grade washing & stain removal", "Scheduled pickup & delivery", "Flexible volume, no long-term commitments", "Priority support for business clients"],
    features_es: ["Programas para empresas de todos los tamaños", "Lavado comercial y eliminación de manchas", "Recogida y entrega programadas", "Volumen flexible, sin compromisos a largo plazo", "Soporte prioritario para clientes empresariales"],
    cta_label: "REQUEST A QUOTE",
    cta_label_es: "SOLICITAR COTIZACIÓN",
    cta_url: "/request-quote",
    bg_image_url: "https://images.unsplash.com/photo-1545173168-9f1947eebb7f?w=600&auto=format&fit=crop",
    tint: "rgba(3, 15, 40, 0.68)",
  },
  commercial: {
    heading: "Commercial Laundry You Can Depend On.",
    heading_es: "Lavandería Comercial en la que Puedes Confiar.",
    subheading: "Volume, quality, reliability — every single day.",
    subheading_es: "Volumen, calidad, confiabilidad — todos los días.",
    features: ["Restaurants, hotels, spas, gyms, offices", "High-volume processing with commercial equipment", "Specialized care for uniforms and delicates", "Reliable pickup & delivery, strict quality control", "Flexible billing and service plans"],
    features_es: ["Restaurantes, hoteles, spas, gimnasios, oficinas", "Procesamiento de alto volumen con equipo comercial", "Cuidado especializado para uniformes y delicados", "Recogida confiable y control de calidad estricto", "Facturación y planes de servicio flexibles"],
    cta_label: "REQUEST A QUOTE",
    cta_label_es: "SOLICITAR COTIZACIÓN",
    cta_url: "/request-quote",
    bg_image_url: "https://plus.unsplash.com/premium_photo-1664372899366-d5fb20b332d1?w=600&auto=format&fit=crop",
    tint: "rgba(3, 17, 48, 0.74)",
  },
};

// ═══════════════════════════════════════════════════════════════
//  MEMBERSHIP
// ═══════════════════════════════════════════════════════════════
const MembershipCard = ({ plan, price, image, features, isPopular, lbs_allowance, onSelectPlan }) => {
  const { t } = useLocale();
  const [h, setH] = useState(false);
  const [showFeatures, setShowFeatures] = useState(false);
  const [isTouchDevice, setIsTouchDevice] = useState(false);
  const [imageError, setImageError] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);

  useEffect(() => {
    setIsTouchDevice('ontouchstart' in window || navigator.maxTouchPoints > 0);
  }, []);

  const priceNum = typeof price === "string" ? price.split("/")[0].trim() : price;

  const featureIcon = (f = "") => {
    const fl = f.toLowerCase();
    if (fl.includes("standard") || fl.includes("estándar") || fl.includes("36h") || fl.includes("turnaround"))
      return <Clock className="w-4 h-4 flex-shrink-0 text-slate-400 mt-0.5" />;
    if (fl.includes("priority") || fl.includes("priorit") || fl.includes("handling"))
      return <Zap className="w-4 h-4 flex-shrink-0 text-amber-400 mt-0.5" />;
    if (fl.includes("emergency") || fl.includes("emergencia") || fl.includes("pickup"))
      return <Truck className="w-4 h-4 flex-shrink-0 text-sky-400 mt-0.5" />;
    if (fl.includes("perfect") || fl.includes("ideal") || fl.includes("great") || fl.includes("best") || fl.includes("airbnb") || fl.includes("families") || fl.includes("professionals"))
      return <Star className="w-4 h-4 flex-shrink-0 text-slate-300 mt-0.5" />;
    return <Check className="w-4 h-4 flex-shrink-0 text-sky-400 mt-0.5" />;
  };

  const redirectToMembership = () => {
    if (onSelectPlan) {
      onSelectPlan(plan, price, lbs_allowance, features);
    } else {
      window.location.href = `/membership-signup?plan=${encodeURIComponent(plan)}&price=${encodeURIComponent(price)}&lbs=${lbs_allowance || ''}`;
    }
  };

  const getDefaultGradient = () => {
    const gradients = ["from-sky-100 to-sky-200", "from-indigo-100 to-indigo-200", "from-purple-100 to-purple-200", "from-pink-100 to-pink-200", "from-amber-100 to-amber-200", "from-emerald-100 to-emerald-200"];
    return gradients[(plan?.length || 0) % gradients.length];
  };

  const visibleFeatures = showFeatures ? features : features.slice(0, 2);

  return (
    <Tilt depth={isPopular ? 4 : 3}>
      <div
        onClick={() => { if (!isTouchDevice) redirectToMembership(); }}
        onMouseEnter={() => { setH(true); if (!isTouchDevice) setShowFeatures(true); }}
        onMouseLeave={() => { setH(false); if (!isTouchDevice) setShowFeatures(false); }}
        onTouchStart={(e) => { if (e.target.closest('button')) return; if (isTouchDevice) { e.preventDefault(); e.stopPropagation(); setShowFeatures(s => !s); } }}
        className={`relative bg-white rounded-2xl overflow-hidden h-full flex flex-col transition-all duration-300 ${!isTouchDevice ? 'cursor-pointer' : ''} ${isPopular ? "border-2 border-primary shadow-2xl shadow-primary/20 sm:scale-105 z-10" : "border border-slate-200 shadow-lg hover:shadow-xl"} ${h ? "-translate-y-1" : ""}`}
        role={!isTouchDevice ? "button" : undefined}
        tabIndex={!isTouchDevice ? 0 : undefined}
        onKeyDown={(e) => { if (!isTouchDevice && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); redirectToMembership(); } }}
      >
        {isPopular && (
          <div className="absolute top-3 left-1/2 -translate-x-1/2 z-20 pointer-events-none">
            <div className="flex items-center gap-1.5 bg-gradient-to-r from-primary to-sky-400 text-white px-3 sm:px-4 py-1.5 rounded-full text-[11px] font-black uppercase tracking-widest shadow-lg whitespace-nowrap">
              <Star className="w-3 h-3 fill-white flex-shrink-0" />{t("MOST POPULAR", "MÁS POPULAR")}
            </div>
          </div>
        )}
        <div className={`relative h-40 w-full overflow-hidden bg-gradient-to-br flex-shrink-0 ${getDefaultGradient()}`}>
          {image && !imageError ? (
            <>
              {!imageLoaded && (
                <div className="absolute inset-0 flex items-center justify-center bg-slate-100 z-10">
                  <div className="w-6 h-6 border-2 border-primary/30 border-t-primary rounded-full animate-spin" />
                </div>
              )}
              <img
                src={image}
                alt={`${plan} – Ventura Fresh Laundry`}
                className={`absolute inset-0 w-full h-full object-cover object-center transition-all duration-500 ${h ? "scale-105" : "scale-100"} ${imageLoaded ? "opacity-100" : "opacity-0"}`}
                loading="lazy"
                onLoad={() => setImageLoaded(true)}
                onError={(e) => { setImageError(true); setImageLoaded(true); e.currentTarget.style.display = "none"; }}
              />
            </>
          ) : (
            <div className="absolute inset-0 flex flex-col items-center justify-center">
              <div className="w-16 h-16 rounded-full bg-white/30 backdrop-blur-sm flex items-center justify-center mb-2"><span className="text-3xl">🧺</span></div>
              <span className="text-xs font-semibold text-slate-600 bg-white/50 px-2 py-1 rounded-full">{plan?.split(' ').slice(0, 2).join(' ') || "Plan"}</span>
            </div>
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-white via-white/20 to-transparent pointer-events-none" />
          <div className="absolute bottom-0 left-0 right-0 px-4 pb-2 pointer-events-none">
            <h2 className={`text-base sm:text-lg font-black transition-colors duration-200 ${h ? "text-primary" : "text-slate-900"}`}>{plan}</h2>
          </div>
        </div>
        <div className="px-4 sm:px-5 pb-5 pt-2 flex flex-col flex-grow">
          <div className="flex items-baseline gap-1.5 mb-3">
            <span className={`text-2xl sm:text-3xl font-black leading-none transition-colors duration-200 ${h ? "text-primary" : "text-slate-900"}`}>{priceNum}</span>
            <span className="text-sm font-semibold text-slate-400 pb-1">/ {t("month", "mes")}</span>
          </div>
          {lbs_allowance && (
            <div className="inline-flex items-center gap-1.5 bg-primary text-white rounded-full px-3 py-1.5 mb-3 self-start pointer-events-none">
              <span className="text-xs font-bold leading-none">{t(`Up to ${lbs_allowance} lbs included`, `Hasta ${lbs_allowance} lbs incluidas`)}</span>
            </div>
          )}
          {features.length > 2 && !showFeatures && (
            <div className="text-center mb-2">
              <span className="text-[10px] text-slate-400 bg-slate-100 px-2 py-1 rounded-full inline-flex items-center gap-1">
                <span className="animate-pulse">👆</span>{isTouchDevice ? t("Tap to see more", "Toca para ver más") : t("Hover to see more", "Pasa el mouse para ver más")}
              </span>
            </div>
          )}
          <div className="overflow-hidden transition-all duration-300 ease-in-out" style={{ maxHeight: showFeatures ? `${Math.min(features.length * 36, 300)}px` : '76px' }}>
            <ul className="space-y-2.5">
              {visibleFeatures.map((f, i) => (
                <li key={i} className="flex items-start gap-2.5 text-xs sm:text-sm text-slate-600 leading-snug">
                  {featureIcon(f)}<span className="flex-1">{f}</span>
                </li>
              ))}
            </ul>
          </div>
          {features.length > 2 && !showFeatures && (
            <div className="text-center mt-1"><span className="text-[10px] text-primary/60 font-medium">+{features.length - 2} {t("more", "más")}...</span></div>
          )}
          <button
            onClick={(e) => { e.stopPropagation(); redirectToMembership(); }}
            className="mt-4 w-full bg-gradient-to-r from-primary to-primary/90 text-white py-2.5 rounded-lg font-semibold hover:from-primary/90 hover:to-primary transition-all duration-200 text-sm shadow-md hover:shadow-lg active:scale-95"
          >
            {t("👉 BECOME A MEMBER", "👉 CONVIÉRTETE EN MIEMBRO")}
          </button>
        </div>
      </div>
    </Tilt>
  );
};

const MembershipExtrasSection = ({ t }) => (
  <div className="mt-8 sm:mt-10 grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6 max-w-5xl mx-auto">
    <div className="bg-white rounded-2xl border border-slate-100 shadow-md p-5 sm:p-6 hover:shadow-lg hover:-translate-y-0.5 transition-all duration-300">
      <div className="flex items-center gap-3 mb-5">
        <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0"><Sparkles className="w-5 h-5 text-primary" /></div>
        <h4 className="font-black text-slate-900 uppercase tracking-wide text-sm">{t("Upgrade Options", "Opciones de Mejora")}</h4>
      </div>
      <div className="space-y-1">
        <div className="flex items-center justify-between py-3 border-b border-slate-50">
          <div className="flex items-center gap-2.5"><span className="w-2.5 h-2.5 rounded-full bg-violet-400 flex-shrink-0" /><span className="text-sm text-slate-700">{t("Premium Service (24h)", "Servicio Premium (24h)")}</span></div>
          <span className="text-sm font-black text-slate-800">+$0.25<span className="text-xs font-normal text-slate-400">/lb</span></span>
        </div>
        <div className="flex items-center justify-between py-3">
          <div className="flex items-center gap-2.5"><span className="w-2.5 h-2.5 rounded-full bg-rose-400 flex-shrink-0" /><span className="text-sm text-slate-700">{t("Express Service (Same Day)", "Servicio Express (Mismo Día)")}</span></div>
          <span className="text-sm font-black text-slate-800">+$0.50<span className="text-xs font-normal text-slate-400">/lb</span></span>
        </div>
      </div>
      <p className="text-xs text-slate-400 mt-3 pt-3 border-t border-slate-50">{t("Need faster service? Upgrade anytime.", "¿Necesitas más rapidez? Mejora en cualquier momento.")}</p>
    </div>
    <div className="bg-white rounded-2xl border border-slate-100 shadow-md p-5 sm:p-6 hover:shadow-lg hover:-translate-y-0.5 transition-all duration-300">
      <div className="flex items-center gap-3 mb-5">
        <div className="w-10 h-10 rounded-xl bg-sky-50 flex items-center justify-center flex-shrink-0">
          <svg className="w-5 h-5 text-sky-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M3 6l9-3 9 3M3 6v12l9 3 9-3V6M12 3v18" /></svg>
        </div>
        <h4 className="font-black text-slate-900 uppercase tracking-wide text-sm">{t("Additional lbs After Allowance", "Lbs Adicionales Después del Límite")}</h4>
      </div>
      <div className="space-y-1">
        {[{ dot: "bg-blue-500", label: t("Standard", "Estándar"), price: "$2.75" }, { dot: "bg-violet-500", label: t("Premium", "Premium"), price: "$3.00" }, { dot: "bg-rose-400", label: t("Express", "Express"), price: "$3.25" }].map((row, i, arr) => (
          <div key={i} className={`flex items-center justify-between py-3 ${i < arr.length - 1 ? "border-b border-slate-50" : ""}`}>
            <div className="flex items-center gap-2.5"><span className={`w-2.5 h-2.5 rounded-full ${row.dot} flex-shrink-0`} /><span className="text-sm text-slate-700">{row.label}</span></div>
            <span className="text-sm font-black text-slate-800">{row.price}<span className="text-xs font-normal text-slate-400">/lb</span></span>
          </div>
        ))}
      </div>
      <p className="text-xs text-slate-400 mt-3 pt-3 border-t border-slate-50">{t("Additional charges apply per pound.", "Se aplican cargos adicionales por libra.")}</p>
    </div>
  </div>
);

const MembershipIncludedSection = ({ t }) => {
  const items = [
    { icon: "🛡️", label: t("Professional Wash, Dry & Fold", "Lavado, Secado y Doblado Profesional") },
    { icon: "🧴", label: t("High-Quality Detergents (Free & Clear Available)", "Detergentes de Alta Calidad") },
    { icon: "👕", label: t("Care for All Fabric Types", "Cuidado para Todo Tipo de Tela") },
    { icon: "📦", label: t("Neatly Folded & Ready to Use", "Perfectamente Doblado y Listo") },
    { icon: "✅", label: t("Consistent & Reliable Service", "Servicio Consistente y Confiable") },
    { icon: "❤️", label: t("100% Satisfaction Guaranteed", "100% Satisfacción Garantizada") },
  ];
  return (
    <div className="mt-6 sm:mt-8 max-w-5xl mx-auto">
      <div className="bg-gradient-to-br from-sky-50 to-white rounded-2xl border border-sky-100 shadow-sm p-6 sm:p-8">
        <h4 className="text-center text-sm font-black uppercase tracking-widest text-primary mb-6">{t("What's Included in All Memberships", "Qué Incluyen Todas las Membresías")}</h4>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-5">
          {items.map((item, i) => (
            <div key={i} className="flex flex-col items-center text-center gap-2.5">
              <div className="w-12 h-12 rounded-2xl bg-white shadow-sm border border-sky-100 flex items-center justify-center text-2xl">{item.icon}</div>
              <span className="text-xs font-semibold text-slate-600 leading-tight">{item.label}</span>
            </div>
          ))}
        </div>
        <p className="text-center text-xs text-slate-400 mt-6 pt-4 border-t border-sky-100">{t("Memberships are prepaid. No monthly commitments.", "Las membresías son prepagadas. Sin compromisos mensuales.")}</p>
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════
//  MAIN PAGE
// ═══════════════════════════════════════════════════════════════
export default function ServicesPage() {
  const { t, locale } = useLocale();
  useSEO({
    title: t(
      "Laundry Services in Ventura, CA | Pickup & Delivery, Wash & Fold, Dry Cleaning, Sneaker Cleaning",
      "Servicios de Lavandería en Ventura, CA | Recogida y Entrega, Lavado y Doblado, Tintorería"
    ),
    description: t(
      "Explore every Ventura Fresh Laundry service: pickup & delivery, wash & fold, dry cleaning, self-service laundromat, sneaker cleaning and commercial/Airbnb laundry in Ventura & Oxnard.",
      "Conoce todos los servicios de Ventura Fresh Laundry: recogida y entrega, lavado y doblado, tintorería, autoservicio, limpieza de tenis y lavandería comercial en Ventura y Oxnard."
    ),
    path: "/services",
  });
  const [openAccordions, setOpenAccordions] = useState({ b2b: 0, commercial: 0, airbnb: 0 });
  const [membershipSection, setMembershipSection] = useState(null);
  const [membershipPlans, setMembershipPlans] = useState([]);
  const [scrollY, setScrollY] = useState(0);
  const { ring, dot } = useCursor();

  useEffect(() => {
    document.body.style.overflowY = "auto";
    document.documentElement.style.overflowY = "auto";
    return () => { document.body.style.overflowY = ""; document.documentElement.style.overflowY = ""; };
  }, []);

  useEffect(() => {
    let tick = false;
    const fn = () => { if (!tick) { requestAnimationFrame(() => { setScrollY(window.pageYOffset); tick = false; }); tick = true; } };
    window.addEventListener("scroll", fn, { passive: true });
    return () => window.removeEventListener("scroll", fn);
  }, []);

  const MEMBERSHIP_SECTION_DEFAULT = {
    heading: t("Flexible Plans for Every Home", "Planes flexibles para cada hogar"),
    subheading: null,
    special_title: t("🎉 New Member Special", "🎉 Oferta para nuevos miembros"),
    special_text: t("$10 OFF your first month on any membership.", "$10 de descuento en tu primer mes en cualquier membresía."),
    cta_title: t("Need help choosing?", "¿Necesitas ayuda para elegir?"),
    cta_text: t("Just call, text, or email us at", "Solo llama, envíanos un mensaje o escríbenos a"),
    cta_button_label: t("👉 BECOME A MEMBER", "👉 CONVIÉRTETE EN MIEMBRO"),
    cta_button_url: "/membership",
    contact_phone: "(820) 234-8181",
    is_active: true,
  };

  const DEFAULT_MEMBERSHIP_PLANS = [
    { plan: t("SOLO ESSENTIAL", "SOLO ESSENTIAL"), price: "$149 / month", image: "https://images.unsplash.com/photo-1462556791646-c201b8241a94?q=80&w=1165&auto=format&fit=crop", features: [t("Included: Standard Service (36h turnaround)", "Incluido: Servicio Estándar (36h de entrega)"), t("Perfect for individuals & couples.", "Perfecto para individuos y parejas.")], isPopular: false, lbs_allowance: 60 },
    { plan: t("FAMILY PLUS", "FAMILY PLUS"), price: "$219 / month", image: "https://images.unsplash.com/photo-1462556791646-c201b8241a94?q=80&w=1165&auto=format&fit=crop", features: [t("Included: Standard Service (36h turnaround)", "Incluido: Servicio Estándar (36h de entrega)"), t("Priority Scheduling Included", "Programación Prioritaria Incluida"), t("Great for families, busy households & short-term rentals.", "Ideal para familias, hogares ocupados y alquileres.")], isPopular: false, lbs_allowance: 90 },
    { plan: t("ELITE CONCIERGE", "ELITE CONCIERGE"), price: "$299 / month", image: "https://images.squarespace-cdn.com/content/v1/696c559a4b2b9b1b0febf8d7/13a4c501-7792-4f72-bf5c-072f95b5f995/ELITE+CONCIERGE.png", features: [t("Included: Standard Service (36h turnaround)", "Incluido: Servicio Estándar (36h de entrega)"), t("Priority Handling & Saved Preferences", "Manejo Prioritario y Preferencias Guardadas"), t("1 Emergency Pickup Included", "1 Recogida de Emergencia Incluida"), t("Ideal for large families, busy professionals & high-volume laundry needs.", "Ideal para familias grandes y profesionales ocupados.")], isPopular: true, lbs_allowance: 120 },
    { plan: t("ULTRA VOLUME", "ULTRA VOLUME"), price: "$379 / month", image: "https://images.unsplash.com/photo-1462556791646-c201b8241a94?q=80&w=1165&auto=format&fit=crop", features: [t("Included: Standard Service (36h turnaround)", "Incluido: Servicio Estándar (36h de entrega)"), t("Priority Handling & Saved Preferences", "Manejo Prioritario y Preferencias Guardadas"), t("2 Emergency Pickups Included", "2 Recogidas de Emergencia Incluidas"), t("Ideal for Airbnb hosts, large households & commercial needs.", "Ideal para anfitriones Airbnb, hogares grandes y usos comerciales.")], isPopular: false, lbs_allowance: 150 },
  ];

  const PER_PIECE_CATEGORIES = [
    { category: t("Home Essentials", "Artículos del hogar"), items: [{ name: t("Bath Mat", "Tapete de baño"), price: "$8.00" }, { name: t("Heavy Rubber Bath Mat", "Tapete de goma pesado"), price: "$13.00" }, { name: t("Oven Mitt", "Cojín para horno"), price: "$8.00" }, { name: t("Pet Bed (Small)", "Cama mascotas (S)"), price: "$15.00" }, { name: t("Pet Bed (M/L)", "Cama mascotas (M/L)"), price: "$18.00" }] },
    { category: t("Bedding", "Ropa de cama"), items: [{ name: t("Standard Pillow", "Almohada estándar"), price: "$10.00" }, { name: t("Large Pillow", "Almohada grande"), price: "$15.00" }, { name: t("Duvet Cover", "Funda de edredón"), price: "$15.00" }, { name: t("Blanket (Small)", "Manta (pequeña)"), price: "$15.00" }, { name: t("Blanket (Large/Heavy)", "Manta (grande/pesada)"), price: "$25.00" }] },
    { category: t("Comforters", "Edredones"), items: [{ name: t("Comforter Twin/Full", "Edredón Twin/Full"), price: "$22.00" }, { name: t("Comforter Queen", "Edredón Queen"), price: "$25.00" }, { name: t("Comforter King", "Edredón King"), price: "$30.00" }, { name: t("Mattress Cover", "Cubrecama"), price: "$25.00" }, { name: t("Down Comforter (Goose Down)", "Edredón de plumas"), price: "$40.00" }] },
    { category: t("Add-on Services", "Servicios adicionales"), items: [{ name: t("Same Day Service", "Servicio mismo día"), price: "$10.00" }, { name: t("Express Service", "Servicio Express"), price: "$15.00" }, { name: t("Hypoallergenic Detergent", "Detergente hipoalergénico"), price: "$5.00" }, { name: t("Premium Softener", "Suavizante premium"), price: "$4.00" }, { name: t("Pet Hair Removal", "Eliminación de pelo de mascotas"), price: "$10.00" }, { name: t("Heavy Soil Treatment", "Tratamiento de suciedad intensa"), price: "$15.00" }, { name: t("Oversized Item Fee", "Cargo por artículo grande"), price: "$10.00" }] },
  ];

  const WASHER_PRICES = [
    { size: t("20 lb (2 loads)", "20 lb (2 cargas)"), price: "$3.25" },
    { size: t("30 lb (3 loads)", "30 lb (3 cargas)"), price: "$4.50" },
    { size: t("50 lb (4 loads)", "50 lb (4 cargas)"), price: "$6.00" },
    { size: t("60 lb (6 loads)", "60 lb (6 cargas)"), price: "$7.50" },
    { size: t("90 lb (9 loads)", "90 lb (9 cargas)"), price: "$9.00" },
  ];
  const DRYER_PRICES = [
    { size: "30 lb", time: "10min", price: "$0.50" },
    { size: "50 lb", time: "8min", price: "$0.50" },
  ];

  const toggleAcc = (sec, idx) => setOpenAccordions(p => ({ ...p, [sec]: p[sec] === idx ? -1 : idx }));

  useEffect(() => {
    const load = async () => {
      try {
        const [sR, pR] = await Promise.all([
          fetch(`${API}/public/membership-section`),
          fetch(`${API}/public/membership-plans`),
        ]);
        if (sR.ok) {
          const d = await sR.json();
          setMembershipSection({ ...d, heading: t(d.heading, d.heading) });
        } else { setMembershipSection(MEMBERSHIP_SECTION_DEFAULT); }
        if (pR.ok) {
          const d = await pR.json();
          setMembershipPlans(d.map(p => ({ plan: t(p.name, p.name), price: p.price, image: p.image_url, features: (p.features || []).map(f => t(f, f)), isPopular: p.is_popular, lbs_allowance: p.lbs_allowance || null })));
        } else { setMembershipPlans(DEFAULT_MEMBERSHIP_PLANS); }
      } catch {
        setMembershipSection(MEMBERSHIP_SECTION_DEFAULT);
        setMembershipPlans(DEFAULT_MEMBERSHIP_PLANS);
      }
    };
    load();
  }, [locale, t]);

  const handleSelectPlan = (planName, planPrice, lbsAllowance, featuresList) => {
    sessionStorage.setItem('selectedMembershipPlan', JSON.stringify({ name: planName, price: planPrice, lbs_allowance: lbsAllowance, features: featuresList }));
    window.location.href = '/membership';
  };

  const MS = membershipSection || MEMBERSHIP_SECTION_DEFAULT;
  const plans = membershipPlans.length > 0 ? membershipPlans : DEFAULT_MEMBERSHIP_PLANS;

  const marqueeItems = [
    t("Pickup & Delivery", "Recogida y Entrega"),
    t("Wash & Fold", "Lavado y Doblado"),
    t("Airbnb Specialists", "Especialistas Airbnb"),
    t("B2B Solutions", "Soluciones B2B"),
    t("Self Service", "Autoservicio"),
    t("Ventura County", "Condado de Ventura"),
  ];

  const darkCtaClass = "inline-flex items-center gap-2 overflow-hidden relative bg-white rounded-full px-7 sm:px-10 py-3.5 sm:py-4 text-sm font-bold uppercase tracking-widest shadow-xl cursor-pointer hover:-translate-y-0.5 transition-transform duration-300 active:scale-95 group touch-manipulation";

  return (
    <>
      <style>{`
        body { overflow-x: hidden; width: 100%; overflow-y: auto !important; }
        html { overflow-y: auto !important; }
        .line-clamp-2 { display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden; }
        .line-clamp-3 { display: -webkit-box; -webkit-line-clamp: 3; -webkit-box-orient: vertical; overflow: hidden; }
        @keyframes fadeUp { from { opacity: 0; transform: translateY(14px); } to { opacity: 1; transform: translateY(0); } }
      `}</style>

      {/* Custom cursor — desktop only */}
      <div className="pointer-events-none fixed inset-0 z-[9999] hidden lg:block">
        <div ref={ring} className="absolute w-9 h-9 rounded-full border border-primary/50 will-change-transform" style={{ top: 0, left: 0 }} />
        <div ref={dot} className="absolute w-1.5 h-1.5 rounded-full bg-primary will-change-transform" style={{ top: 0, left: 0 }} />
      </div>

      <div className="min-h-screen bg-white overflow-x-hidden">
        <PublicNav />

        {/* HERO */}
        <section className="relative min-h-[55vh] sm:min-h-[65vh] flex items-end justify-center overflow-hidden">
          <div className="absolute inset-0 will-change-transform" style={{ backgroundImage: "url('https://images.unsplash.com/photo-1545173168-9f1947eebb7f?w=1920&h=1080&fit=crop')", backgroundSize: "cover", backgroundPosition: "center", transform: `translateY(${scrollY * 0.18}px) scale(1.08)` }} />
          <div className="absolute inset-0 bg-gradient-to-t from-slate-950/92 via-slate-900/65 to-slate-800/30" />
          <div className="absolute inset-0" style={{ background: "radial-gradient(ellipse at center,transparent 40%,rgba(0,0,0,0.5) 100%)" }} />
          <div className="relative z-10 text-center px-4 sm:px-6 pb-12 sm:pb-20 max-w-5xl mx-auto w-full">
            <div className="inline-flex items-center gap-2 px-3 sm:px-4 py-2 rounded-full bg-white/10 backdrop-blur-md border border-white/20 mb-5 sm:mb-7" style={{ animation: "fadeUp 0.8s 0.1s both ease-out" }}>
              <span className="w-1.5 h-1.5 rounded-full bg-primary animate-pulse flex-shrink-0" />
              <span className="text-[11px] text-white/65 font-bold uppercase tracking-[0.14em] sm:tracking-[0.18em]">{t("Our Services", "Nuestros Servicios")}</span>
            </div>
            <h1 className="text-3xl sm:text-5xl md:text-6xl lg:text-7xl font-bold text-white leading-[1.08] mb-3 sm:mb-4 tracking-tight" style={{ animation: "fadeUp 0.9s 0.25s both ease-out" }}>
              {t("A clean space for", "Un espacio limpio para")}
              <span className="block font-bold">{t("everyone.", "todos.")}</span>
            </h1>
            <p className="text-sm sm:text-lg md:text-xl text-white/65 max-w-xl mx-auto font-medium" style={{ animation: "fadeUp 0.9s 0.4s both ease-out" }}>
              {t("Self-service, wash & fold, pickup & delivery — tailored to your life.", "Autoservicio, lavado y doblado, recogida y entrega — adaptado a tu vida.")}
            </p>
          </div>
          <div className="absolute bottom-0 left-0 right-0 z-20">
            <svg viewBox="0 0 1440 90" preserveAspectRatio="none" className="w-full h-7 sm:h-14 lg:h-20"><path d="M0,45 C300,0 600,90 1440,45 L1440,90 L0,90 Z" fill="white" /></svg>
          </div>
        </section>

        <Marquee items={marqueeItems} />

        {/* CORE SERVICES */}
        <section className="py-12 sm:py-20 lg:py-24 relative overflow-hidden bg-white">
          <div className="relative z-10 max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-12">
            <Reveal dir="blur"><p className="text-center text-[11px] font-bold uppercase tracking-[0.18em] text-primary/50 mb-3">{t("Core Services", "Servicios Principales")}</p></Reveal>
            <Reveal delay={80}><h2 className="text-2xl sm:text-4xl lg:text-5xl font-bold text-slate-900 text-center mb-3 leading-tight">{t("Choose the option", "Elige la opción")}<span className="block text-primary font-bold">{t("that fits your day.", "que se adapte a tu día.")}</span></h2></Reveal>
            <Reveal delay={160}><p className="text-slate-500 text-center mb-6 sm:mb-8 max-w-xl mx-auto text-sm sm:text-lg">{t("Walk-in, drop-off, or pickup & delivery — we've got you covered.", "Presencial, entrega o recogida — aquí estamos.")}</p></Reveal>

            <div className="flex gap-5 xl:gap-6 items-start">
              <AdBannerVertical config={AD_CONFIG.left} />
              <div className="flex-1 min-w-0">
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 sm:gap-6 mb-4 sm:mb-8">
                  <Reveal delay={0} dir="up" dur={700}><PickupDeliveryServiceCardHead t={t} /></Reveal>
                  <Reveal delay={80} dir="up" dur={700}><WashFoldServiceCardHead t={t} /></Reveal>
                  <Reveal delay={160} dir="up" dur={700}><SneakerServiceCardHead t={t} /></Reveal>
                </div>
                <Reveal delay={160} dir="up" dur={700}>
                  <Tilt depth={3}>
                    <div className="relative bg-white rounded-2xl p-5 sm:p-8 border border-slate-100 shadow-lg overflow-hidden group hover:-translate-y-0.5 hover:shadow-xl hover:border-primary/20 transition-all duration-300">
                      <div className="absolute top-0 left-8 right-8 h-px bg-gradient-to-r from-transparent via-primary to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                      <div className="absolute inset-0 bg-gradient-to-br from-sky-50/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                      <div className="relative flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                        <div>
                          <div className="flex items-center gap-2.5 mb-2">
                            <div className="w-10 h-10 flex items-center justify-center text-xl bg-slate-50 group-hover:bg-primary/10 rounded-2xl transition-colors duration-300 flex-shrink-0">🏪</div>
                            <h3 className="text-lg sm:text-2xl font-bold text-slate-900 group-hover:text-primary transition-colors duration-200">{t("Self-Service Laundry", "Lavandería de Autoservicio")}</h3>
                          </div>
                          <p className="text-slate-500 text-sm pl-[50px] sm:pl-[52px]">{t("Modern machines, fast dryers, hassle-free experience.", "Máquinas modernas, secadoras rápidas, sin complicaciones.")}</p>
                        </div>
                        <div className="flex gap-5 sm:gap-8 flex-shrink-0 pl-[50px] sm:pl-0">
                          {[{ val: "6:00 AM", label: t("Open", "Abrimos") }, { val: "10:00 PM", label: t("Close", "Cerramos") }].map((h, i) => (
                            <div key={i} className="text-center"><div className="text-lg sm:text-2xl font-black text-primary">{h.val}</div><div className="text-[11px] text-slate-400 uppercase tracking-wider font-semibold">{h.label}</div></div>
                          ))}
                        </div>
                      </div>
                      <div className="relative grid grid-cols-1 sm:grid-cols-3 gap-2 mt-4 pt-4 border-t border-slate-100">
                        {[t("Clean & well-maintained", "Limpio y bien mantenido"), t("High-performance washers", "Lavadoras de alto rendimiento"), t("Fast-drying machines", "Secadoras rápidas")].map((item, i) => (
                          <div key={i} className="flex items-center gap-2 text-sm text-slate-600"><Check className="h-4 w-4 text-sky-400 flex-shrink-0" />{item}</div>
                        ))}
                      </div>
                    </div>
                  </Tilt>
                </Reveal>
              </div>
              <AdBannerVertical config={AD_CONFIG.right} />
            </div>
            <Reveal delay={120} dir="up">
              <DynamicExpressServiceBlock t={t} />
            </Reveal>
          </div>
        </section>

        {/* DYNAMIC ADMIN SERVICES */}
        <AdminServicesSection t={t} locale={locale} />

        {/* MEMBERSHIP */}
        <section className="py-12 sm:py-20 lg:py-24 bg-gradient-to-b from-slate-50/60 to-white relative overflow-hidden">
          <div className="absolute inset-0 opacity-[0.4]" style={{ backgroundImage: "radial-gradient(rgba(14,165,233,0.08) 1px,transparent 1px)", backgroundSize: "24px 24px" }} />
          <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 lg:px-12">
            <Reveal dir="blur"><p className="text-center text-[11px] font-bold uppercase tracking-[0.18em] text-primary/50 mb-3">{t("Membership", "Membresía")}</p></Reveal>
            <Reveal delay={80}><h2 className="text-2xl sm:text-4xl lg:text-5xl font-bold text-slate-900 text-center mb-3">{MS.heading}</h2></Reveal>
            {MS.subheading && <Reveal delay={140}><p className="text-slate-500 text-center text-sm sm:text-lg mb-5">{MS.subheading}</p></Reveal>}
            {(MS.special_title || MS.special_text) && (
              <Reveal delay={180} dir="scale">
                <div className="relative overflow-hidden bg-gradient-to-r from-amber-50 to-yellow-50 rounded-2xl p-5 sm:p-6 max-w-2xl mx-auto border border-amber-200/60 mb-8 sm:mb-12 shadow-sm">
                  <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-amber-400/50 to-transparent" />
                  {MS.special_title && <h3 className="text-lg sm:text-2xl font-bold text-amber-800 mb-2">{MS.special_title}</h3>}
                  {MS.special_text && <p className="text-amber-700 text-sm sm:text-base leading-relaxed">{MS.special_text}</p>}
                </div>
              </Reveal>
            )}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 max-w-7xl mx-auto">
              {plans.map((plan, i) => (
                <Reveal key={i} delay={i * 80} dir="up" dur={700}>
                  <MembershipCard plan={plan.plan} price={plan.price} image={plan.image} features={plan.features} isPopular={plan.isPopular} lbs_allowance={plan.lbs_allowance} onSelectPlan={handleSelectPlan} />
                </Reveal>
              ))}
            </div>
            <Reveal delay={200} dir="up"><MembershipExtrasSection t={t} /></Reveal>
            <Reveal delay={260} dir="up"><MembershipIncludedSection t={t} /></Reveal>
            <Reveal delay={320} dir="scale">
              <div className="text-center bg-white rounded-2xl p-5 sm:p-8 shadow-lg max-w-2xl mx-auto border border-slate-100 hover:shadow-xl transition-shadow duration-300 mt-8 sm:mt-10">
                <div className="w-10 h-10 sm:w-12 sm:h-12 mx-auto mb-4 bg-primary/10 rounded-2xl flex items-center justify-center"><Shield className="w-5 h-5 sm:w-6 sm:h-6 text-primary" /></div>
                {MS.cta_title && <h4 className="text-base sm:text-lg font-bold text-slate-900 mb-2">{MS.cta_title}</h4>}
                {MS.cta_text && (
                  <p className="text-slate-500 text-sm mb-5 sm:mb-6">
                    {MS.cta_text}{" "}
                    {MS.contact_phone && <a href={`tel:${MS.contact_phone.replace(/[^\d]/g, "")}`} className="text-primary font-bold hover:underline">{MS.contact_phone}</a>}
                  </p>
                )}
                <div className="flex justify-center">
                  <BecomeAMemberButton label={MS.cta_button_label || t("👉 BECOME A MEMBER", "👉 CONVIÉRTETE EN MIEMBRO")} className="shadow-lg shadow-primary/30" />
                </div>
              </div>
            </Reveal>
          </div>
        </section>

        {/* SECCIONES OSCURAS DINÁMICAS */}
        <DynamicDarkSection sectionType="airbnb" defaultData={DARK_SECTION_DEFAULTS.airbnb} scrollY={scrollY} t={t} />
        <DynamicDarkSection sectionType="b2b" defaultData={DARK_SECTION_DEFAULTS.b2b} scrollY={scrollY} t={t} />
        <DynamicDarkSection sectionType="commercial" defaultData={DARK_SECTION_DEFAULTS.commercial} scrollY={scrollY} t={t} />

        {/* PRICING SECTION */}
        <section className="py-12 sm:py-20 lg:py-24 bg-white">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-12">
            <Reveal dir="blur"><p className="text-center text-[11px] font-bold uppercase tracking-[0.18em] text-primary/50 mb-3">{t("Pricing", "Precios")}</p></Reveal>
            <Reveal delay={80}><h2 className="text-2xl sm:text-4xl lg:text-5xl font-bold text-slate-900 text-center mb-3 leading-tight">{t("Transparent", "Precios")}<span className="block text-primary font-bold">{t("Pricing.", "Transparentes.")}</span></h2></Reveal>
            <Reveal delay={160}><p className="text-slate-500 text-center mb-8 sm:mb-14 max-w-xl mx-auto text-sm sm:text-lg">{t("No surprises. Premium service you can count on.", "Sin sorpresas. Servicio premium en el que puedes confiar.")}</p></Reveal>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6 mb-4 sm:mb-8">
              <Reveal delay={0} dir="up" dur={700}>
                <Tilt depth={4}>
                  <div className="relative bg-white rounded-2xl h-full flex flex-col overflow-hidden border border-slate-100 shadow-lg hover:border-primary/30 hover:shadow-2xl hover:shadow-sky-100/60 transition-all duration-300 group">
                    <div className="absolute top-0 left-6 right-6 h-px bg-gradient-to-r from-transparent via-primary to-transparent opacity-0 group-hover:opacity-100 transition-all duration-500" />
                    <div className="relative px-5 sm:px-7 pt-6 pb-4 border-b border-slate-100">
                      <div className="w-12 h-12 flex items-center justify-center text-2xl mb-3 rounded-2xl bg-slate-50 group-hover:bg-primary/15 group-hover:scale-110 group-hover:rotate-3 transition-all duration-300">🚚</div>
                      <h3 className="text-xl sm:text-2xl font-bold mb-1 text-slate-900 group-hover:text-primary transition-colors duration-200">{t("Pickup & Delivery", "Recogida y Entrega")}</h3>
                      <p className="text-slate-400 text-sm">{t("Laundry, fully automated on your schedule.", "Lavandería automatizada en tu horario.")}</p>
                    </div>
                    <div className="flex-grow divide-y divide-slate-50">
                      {[
                        { plan: t("Standard", "Estándar"), badge: "standard", memberPrice: "$2.50/lb", regularPrice: "$2.75/lb" },
                        { plan: t("Premium", "Premium"), badge: "premium", memberPrice: "$2.75/lb", regularPrice: "$3.00/lb", isPopular: true },
                        { plan: t("Express", "Express"), badge: "express", memberPrice: "$3.00/lb", regularPrice: "$3.25/lb" },
                      ].map((row, i) => (
                        <div key={i} className={`px-5 py-3 ${row.isPopular ? "bg-sky-50/60" : ""}`}>
                          <div className="flex items-center justify-between flex-wrap gap-2">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className={`text-sm font-semibold ${row.isPopular ? "text-primary" : "text-slate-700"}`}>{row.plan}</span>
                              {row.isPopular && <span className="text-[11px] font-bold text-primary bg-primary/10 px-1.5 py-0.5 rounded-full">⭐</span>}
                              <PlanBadge type={row.badge} t={t} />
                            </div>
                            <div className="flex items-center gap-3 ml-auto">
                              <div className="flex items-center gap-1"><Star className="w-3 h-3 text-primary fill-primary/40" /><span className="text-sm font-black text-primary">{row.memberPrice}</span></div>
                              <div className="flex items-center gap-1"><span className="text-xs text-slate-400">{t("Regular", "Regular")}</span><span className="text-sm font-semibold text-slate-500">{row.regularPrice}</span></div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                    <div className="relative px-5 py-3 bg-slate-50 border-t border-slate-100">
                      <ul className="space-y-1">
                        {[t("✅ FREE delivery (0–3 miles)", "✅ Entrega GRATIS (0–3 millas)"), t("🚗 3–15 Miles | $1.99–$8.99", "🚗 3–15 Miles | $1.99–$8.99"), t("📦 Min. order $40", "📦 Pedido mínimo $40")].map((item, i) => (
                          <li key={i} className="text-xs text-slate-500">{item}</li>
                        ))}
                      </ul>
                    </div>
                    <div className="relative px-5 sm:px-7 pb-6 pt-4">
                      <Link to="/schedule-pickup">
                        <button className="group w-full flex items-center justify-center gap-2 bg-primary text-white rounded-xl px-6 py-3.5 text-sm font-bold uppercase tracking-wider hover:bg-primary/90 active:scale-95 transition-all duration-300 shadow-md shadow-primary/20 overflow-hidden relative touch-manipulation" style={{ minHeight: "48px" }}>
                          <span className="relative z-10 flex items-center gap-2">{t("SCHEDULE PICK-UP", "PROGRAMAR RECOGIDA")}<ArrowRight className="w-4 h-4 transition-transform duration-200 group-hover:translate-x-1" /></span>
                          <span className="absolute inset-0 bg-gradient-to-r from-transparent via-white/15 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-700" />
                        </button>
                      </Link>
                    </div>
                  </div>
                </Tilt>
              </Reveal>
              <Reveal delay={80} dir="up" dur={700}>
                <Tilt depth={4}>
                  <div className="relative bg-white rounded-2xl h-full flex flex-col overflow-hidden border border-slate-100 shadow-lg hover:border-primary/30 hover:shadow-2xl hover:shadow-sky-100/60 transition-all duration-300 group">
                    <div className="absolute top-0 left-6 right-6 h-px bg-gradient-to-r from-transparent via-primary to-transparent opacity-0 group-hover:opacity-100 transition-all duration-500" />
                    <div className="relative px-5 sm:px-7 pt-6 pb-4 border-b border-slate-100">
                      <div className="w-12 h-12 flex items-center justify-center text-2xl mb-3 rounded-2xl bg-slate-50 group-hover:bg-primary/15 group-hover:scale-110 group-hover:rotate-3 transition-all duration-300">🧺</div>
                      <h3 className="text-xl sm:text-2xl font-bold mb-1 text-slate-900 group-hover:text-primary transition-colors duration-200">{t("Wash • Dry • Fold", "Lavar • Secar • Doblar")}</h3>
                      <p className="text-slate-400 text-sm">{t("Professional care without lifting a finger.", "Cuidado profesional sin mover un dedo.")}</p>
                    </div>
                    <div className="flex-grow divide-y divide-slate-50">
                      {[
                        { plan: t("Standard", "Estándar"), badge: "standard", price: "$2.25/lb", bestFor: t("Budget-friendly", "Económico") },
                        { plan: t("Premium", "Premium"), badge: "premium", price: "$2.50/lb", bestFor: t("Most popular", "Más popular"), isPopular: true },
                        { plan: t("Express", "Express"), badge: "express", price: "$2.75/lb", bestFor: t("Urgent orders", "Urgentes") },
                      ].map((row, i) => (
                        <div key={i} className={`px-5 py-3 flex items-center justify-between gap-2 ${row.isPopular ? "bg-sky-50/60" : ""}`}>
                          <div className="flex items-center gap-2 flex-wrap min-w-0">
                            <span className={`text-sm font-semibold ${row.isPopular ? "text-primary" : "text-slate-700"}`}>{row.plan}</span>
                            {row.isPopular && <span className="text-[11px] font-bold text-primary bg-primary/10 px-1.5 py-0.5 rounded-full">⭐</span>}
                            <PlanBadge type={row.badge} t={t} />
                          </div>
                          <span className="text-lg font-black text-primary ml-2 whitespace-nowrap">{row.price}</span>
                        </div>
                      ))}
                    </div>
                    <div className="relative px-5 py-2 bg-slate-50 border-t border-slate-100 text-xs text-slate-500">{t("Professional care · Minimum 10 lb per order", "Cuidado profesional · Mínimo 10 lb por orden")}</div>
                    <div className="relative px-5 py-2 bg-slate-50 border-t border-slate-100 text-xs text-slate-500">{t("Monday – Sunday · 8:00 AM – 6:00 PM", "Lunes – Domingo · 8:00 AM – 6:00 PM")}</div>
                    <div className="relative px-5 sm:px-7 pb-6 pt-4">
                      <Link to="/wash-fold">
                        <button className="group w-full flex items-center justify-center gap-2 bg-slate-900 text-white rounded-xl px-6 py-3.5 text-sm font-bold uppercase tracking-wider hover:bg-slate-800 active:scale-95 transition-all duration-300 shadow-md overflow-hidden relative touch-manipulation" style={{ minHeight: "48px" }}>
                          <span className="relative z-10 flex items-center gap-2">{t("DROP OFF / SCHEDULE", "ENTREGA / PROGRAMAR")}<ArrowRight className="w-4 h-4 transition-transform duration-200 group-hover:translate-x-1" /></span>
                          <span className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-700" />
                        </button>
                      </Link>
                    </div>
                  </div>
                </Tilt>
              </Reveal>
              <Reveal delay={160} dir="up" dur={700}>
                <Tilt depth={4}>
                  <div className="relative bg-white rounded-2xl h-full flex flex-col overflow-hidden border border-slate-100 shadow-lg hover:border-violet-300 hover:shadow-2xl hover:shadow-violet-100/60 transition-all duration-300 group">
                    <div className="absolute top-0 left-6 right-6 h-px bg-gradient-to-r from-transparent via-violet-500 to-transparent opacity-0 group-hover:opacity-100 transition-all duration-500" />
                    <div className="relative px-5 sm:px-7 pt-6 pb-4 border-b border-slate-100">
                      <div className="w-12 h-12 flex items-center justify-center text-2xl mb-3 rounded-2xl bg-slate-50 group-hover:bg-violet-100 group-hover:scale-110 group-hover:rotate-3 transition-all duration-300">👟</div>
                      <div className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-violet-100 text-violet-600 text-[10px] font-bold uppercase tracking-wide mb-2">
                        <Sparkles className="w-3 h-3" />{t("AI-Powered", "Con IA")}
                      </div>
                      <h3 className="text-xl sm:text-2xl font-bold mb-1 text-slate-900 group-hover:text-violet-600 transition-colors duration-200">{t("Sneaker & Shoe Cleaning", "Limpieza de Tenis y Calzado")}</h3>
                      <p className="text-slate-400 text-sm">{t("Instant AI price estimate from your photos.", "Precio estimado al instante con IA desde tus fotos.")}</p>
                    </div>
                    <div className="flex-grow divide-y divide-slate-50">
                      {[
                        { plan: t("1st pair", "1er par"), price: "$12.00" },
                        { plan: t("2nd pair", "2do par"), price: "$10.00", isPopular: true },
                        { plan: t("3rd+ pair", "3er par en adelante"), price: "$8.00" },
                      ].map((row, i) => (
                        <div key={i} className={`px-5 py-3 flex items-center justify-between gap-2 ${row.isPopular ? "bg-violet-50/60" : ""}`}>
                          <div className="flex items-center gap-2 flex-wrap min-w-0">
                            <span className={`text-sm font-semibold ${row.isPopular ? "text-violet-600" : "text-slate-700"}`}>{row.plan}</span>
                            {row.isPopular && <span className="text-[11px] font-bold text-violet-600 bg-violet-100 px-1.5 py-0.5 rounded-full">⭐</span>}
                          </div>
                          <span className="text-lg font-black text-violet-600 ml-2 whitespace-nowrap">{row.price}</span>
                        </div>
                      ))}
                    </div>
                    <div className="relative px-5 py-3 bg-slate-50 border-t border-slate-100">
                      <ul className="space-y-1">
                        {[t("✨ +$0–$10 based on soiling/complexity", "✨ +$0–$10 según suciedad/complejidad"), t("🏪 Drop off in-store", "🏪 Entrega en tienda"), t("📸 Photo evidence at every step", "📸 Evidencia fotográfica en cada paso")].map((item, i) => (
                          <li key={i} className="text-xs text-slate-500">{item}</li>
                        ))}
                      </ul>
                    </div>
                    <div className="relative px-5 sm:px-7 pb-6 pt-4">
                      <Link to="/sneaker-quote">
                        <button className="group w-full flex items-center justify-center gap-2 bg-violet-600 text-white rounded-xl px-6 py-3.5 text-sm font-bold uppercase tracking-wider hover:bg-violet-700 active:scale-95 transition-all duration-300 shadow-md shadow-violet-200 overflow-hidden relative touch-manipulation" style={{ minHeight: "48px" }}>
                          <span className="relative z-10 flex items-center gap-2">{t("GET INSTANT QUOTE", "COTIZA YA")}<ArrowRight className="w-4 h-4 transition-transform duration-200 group-hover:translate-x-1" /></span>
                          <span className="absolute inset-0 bg-gradient-to-r from-transparent via-white/15 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-700" />
                        </button>
                      </Link>
                    </div>
                  </div>
                </Tilt>
              </Reveal>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
              <Reveal dir="left" delay={0}>
                <DynamicSelfServiceTable t={t} locale={locale} />
              </Reveal>
              <Reveal dir="right" delay={60}>
                <DynamicPerPieceTable t={t} locale={locale} />
              </Reveal>
            </div>

            <Reveal delay={200} dir="scale">
              <div className="mt-12 sm:mt-16 text-center">
                <p className="text-slate-500 text-sm mb-4">{t("Save on every order with a membership plan.", "Ahorra en cada orden con un plan de membresía.")}</p>
                <BecomeAMemberButton label={t("🌟 Become a Member & Save", "🌟 Conviértete en Miembro y Ahorra")} />
              </div>
            </Reveal>
          </div>
        </section>

        <PublicFooter />
      </div>
    </>
  );
}